import type { AppTranslations } from './types'

export const de = {
  characteristics: {
    common: {
      na: 'K.A.',
      no: 'Nein',
      source: 'Quelle',
      sourceCalculated: 'Berechnet',
      years: 'Jahre',
      yes: 'Ja',
    },
    developmentPotential: {
      tooltips: {
        far: {
          calculation: 'Bruttogeschossfläche geteilt durch Grundstücksfläche. Beispiel: 1000 m² Gebäude auf 500 m² Grundstück = AZ von 2.0',
          categories: [
            {
              description: 'Ländlich, Einfamilienhäuser auf grossen Grundstücken',
              label: 'Geringe Dichte',
              range: 'AZ 0.1-0.5',
            },
            {
              description: 'Vorstädtisch, Reihenhäuser',
              label: 'Mittlere Dichte',
              range: 'AZ 0.5-1.5',
            },
            {
              description: 'Städtisch, mittelhohe Mehrfamilienhäuser',
              label: 'Hohe Dichte',
              range: 'AZ 1.5-3.0',
            },
            {
              description: 'Innenstadt, Hochhäuser',
              label: 'Sehr hohe Dichte',
              range: 'AZ >3.0',
            },
          ],
          importance: 'Die AZ ist die wichtigste Kennzahl für das Verständnis der Bebauungsintensität und der Bodennutzungseffizienz. Eine höhere AZ weist auf eine intensivere Bodennutzung und in der Regel höhere Immobilienwerte pro Quadratmeter Grundstücksfläche hin.',
          limitations: 'Die AZ-Vorschriften variieren je nach Zone. Prüfen Sie die lokalen Nutzungsplanungsdokumente für die maximal zulässige AZ in Ihrer Zone.',
          shortDescription: 'Verhältnis der gesamten Geschossfläche zur Grundstücksfläche',
          source: 'Berechnet aus dem Eidgenössischen Gebäude- und Wohnungsregister (GWR) und dem Grundbuch',
          title: 'Ausnützungsziffer (AZ)',
        },
        floorPotential: {
          calculation: 'Differenz zwischen der maximal zulässigen Geschosszahl (gemäss lokaler Nutzungsplanung) und der aktuellen Geschosszahl',
          importance: 'Zusätzliche Geschosse stellen den direktesten Weg dar, die Gebäudekapazität und den Immobilienwert zu steigern. Jedes zusätzliche Geschoss kann erhebliche Nutzfläche und Miet- oder Verkaufserlöspotenzial bieten.',
          limitations: 'Geschossaufstockungen erfordern eine Tragwerksanalyse, eine Beurteilung der Fundamentkapazität und können Anforderungen an Aufzüge, Brandschutznachrüstungen und zusätzliche Parkplätze auslösen. Die Kosten können erheblich sein.',
          shortDescription: 'Vergleich der aktuellen Geschosszahl mit der maximal zulässigen gemäss Zonenplanung',
          source: 'Lokale Nutzungsplanung (LNP) und Eidgenössisches Gebäude- und Wohnungsregister (GWR)',
          title: 'Geschosszahlpotenzial',
        },
        heightPotential: {
          calculation: 'Differenz zwischen der maximal zulässigen Höhe (gemäss lokaler Nutzungsplanung) und der aktuellen Gebäudehöhe',
          importance: 'Das Verständnis des Höhenpotenzials ist entscheidend für die Beurteilung vertikaler Erweiterungsmöglichkeiten, des Immobilienentwicklungswerts und der Einhaltung lokaler Vorschriften. Zusätzliche Höhe kann die nutzbare Geschossfläche und den Immobilienwert erheblich steigern.',
          limitations: 'Maximale Höhenbeschränkungen können von weiteren Faktoren abhängen, wie Grenzabständen, Beschattungsstudien, Denkmalschutzauflagen oder Einsprachen aus der Nachbarschaft. Eine professionelle Planungsberatung wird empfohlen.',
          shortDescription: 'Vergleich der aktuellen Höhe mit der maximal zulässigen Höhe gemäss lokaler Zonenplanung',
          source: 'Lokale Nutzungsplanung (LNP) und Eidgenössisches Gebäude- und Wohnungsregister (GWR)',
          title: 'Gebäudehöhenpotenzial',
        },
      },
    },
    header: {
      subtitle: 'Detaillierte Informationen zu diesem Gebäude',
      title: 'Gebäudemerkmale',
    },
    neighborhood: {
      ageRanges: {
        '1919_1945': '1919-1945',
        '1946_1960': '1946-1960',
        '1961_1970': '1961-1970',
        '1971_1980': '1971-1980',
        '1981_1990': '1981-1990',
        '1991_2000': '1991-2000',
        '2001_2010': '2001-2010',
        '2011_2020': '2011-2020',
        post2020: 'Nach 2020',
        pre1919: 'Vor 1919',
      },
      badges: {
        buildingOutlier: 'Gebäudeausreisser',
      },
      cards: {
        ageDistribution: {
          title: 'Gebäudealtersverteilung',
        },
        analysis: {
          tooltip: {
            source: 'GWR, Popety.io',
          },
        },
        areaComparison: {
          title: 'Grundflächenvergleich',
          tooltip: {
            calculation: 'Vergleicht die Erdgeschossfläche dieses Gebäudes mit den durchschnittlichen und maximalen Flächen der Nachbargebäude',
            description: 'Wie sich die Grundfläche dieses Gebäudes mit den umliegenden Gebäuden vergleicht',
            importance: 'Die Gebäudegrundfläche im Vergleich zu Nachbarn zeigt die Landnutzungsintensität, Entwicklungsdichte und ob das Gebäude typisch für das Gebiet ist.',
            limitations: 'Die Grundfläche allein spiegelt nicht die Gesamtgrösse des Gebäudes wider. Prüfen Sie die Geschosszahl und Bruttogeschossfläche für eine vollständige Grössenbewertung.',
            title: 'Flächenvergleich mit Nachbarn',
          },
        },
        cohesionBreakdown: {
          title: 'Kohäsionsaufschlüsselung',
          tooltip: {
            calculation: 'Gewichteter Durchschnitt der zeitlichen (40%), physischen (30%), typologischen (20%) und Dichte- (10%) Kohäsionswerte',
            description: 'Zeigt, wie homogen die Nachbarschaft über mehrere Dimensionen hinweg ist',
            importance: 'Ein hoher Kohäsionswert deutet auf ein klar definiertes Quartier mit einheitlichen Gebäudemerkmalen hin',
            title: 'Kohäsionsaufschlüsselung',
          },
        },
        comparativeAnalysis: {
          title: 'Vergleichsanalyse',
        },
        conformityBreakdown: {
          title: 'Gebäudekonformitäts-Aufschlüsselung',
          tooltip: {
            calculation: 'Vergleicht die Eigenschaften dieses Gebäudes mit dem typischen Quartierprofil',
            description: 'Zeigt, wie gut dieses spezifische Gebäude zum Quartiermuster passt',
            importance: 'Niedrige Konformität in einem kohärenten Quartier weist auf ein Ausreissergebäude hin, was Bewertung, Entwicklungsbeschränkungen oder architektonische Harmonie beeinflussen kann.',
            title: 'Gebäudekonformitäts-Aufschlüsselung',
          },
        },
        heightComparison: {
          title: 'Höhenvergleich',
          tooltip: {
            calculation: 'Vergleicht die Gesamthöhe dieses Gebäudes mit den durchschnittlichen und maximalen Höhen der Nachbargebäude innerhalb eines definierten Radius (typischerweise 100-200m)',
            description: 'Wie sich die Höhe dieses Gebäudes mit den umliegenden Gebäuden vergleicht',
            importance: 'Die Gebäudehöhe im Vergleich zu Nachbarn beeinflusst Aussicht, Sonneneinstrahlung, Privatsphäre, architektonische Harmonie und Einhaltung lokaler Zonenvorschriften.',
            limitations: 'Vergleich basierend auf Gebäuden innerhalb des Suchradius. Lokale Topographie und Geländehöhenunterschiede werden in dieser Metrik nicht berücksichtigt.',
            title: 'Höhenvergleich mit Nachbarn',
          },
        },
        neighborhoodAnalysis: {
          title: 'Nachbarschafts- und Gebäudeanalyse',
          tooltip: {
            calculation: 'Nachbarschaftskohäsion misst, wie ähnlich die umliegenden Gebäude sind. Gebäudekonformität misst, wie gut DIESES Gebäude zum Nachbarschaftsmuster passt.',
            description: 'Analysiert sowohl die Nachbarschaftskohäsion als auch wie gut dieses Gebäude in das erkannte Quartier passt',
            importance: 'Hohe Kohäsion mit niedriger Konformität weist auf ein Ausreissergebäude in einer kohärenten Nachbarschaft hin, was Bewertung und Entwicklungspotenzial beeinflussen kann.',
            title: 'Nachbarschafts- und Gebäudeanalyse',
          },
        },
        quartierProfile: {
          title: 'Nachbarschaftsprofil',
        },
        volumeComparison: {
          title: 'Volumenvergleich',
          tooltip: {
            calculation: 'Vergleicht das geschlossene Gesamtvolumen dieses Gebäudes mit den durchschnittlichen und maximalen Volumen der Nachbargebäude',
            description: 'Wie sich das Gesamtvolumen dieses Gebäudes mit den umliegenden Gebäuden vergleicht',
            importance: 'Das Gebäudevolumen ist ein wichtiger Indikator für die Gesamtmasse und Entwicklungsintensität. Wird für Energiemodellierung und HLK-Kapazitätsplanung verwendet.',
            limitations: 'Volumenberechnungen können vereinfachte geometrische Annahmen verwenden. Das tatsächliche Innenvolumen kann aufgrund der Dachgeometrie und Innenaufteilung variieren.',
            title: 'Volumenvergleich mit Nachbarn',
          },
        },
      },
      chart: {
        areaAxis: 'Fläche (m²)',
        buildingsAxis: 'Gebäude',
        heightAxis: 'Höhe (m)',
        neighborhoodAvg: 'Nachbarschaft Durchschn.',
        neighborhoodMax: 'Nachbarschaft Max.',
        thisBuilding: 'Dieses Gebäude',
        volumeAxis: 'Volumen (m³)',
      },
      charts: {
        area: {
          source: 'Popety.io, GWR',
        },
        height: {
          source: 'Popety.io, GWR',
        },
        volume: {
          source: 'Popety.io, GWR',
        },
      },
      cohesion: {
        density: {
          label: 'Dichte (10%)',
          source: 'Popety.io, GWR',
          tooltip: {
            calculation: 'Basierend auf der Anzahl der Gebäude pro Quadratkilometer. Höhere Dichte deutet auf städtisches Quartier hin.',
            description: 'Misst die Entwicklungsdichte',
            title: 'Dichte-Kohäsion',
          },
        },
        physical: {
          label: 'Physische Kohäsion (30%)',
          source: 'Popety.io, GWR',
          tooltip: {
            calculation: 'Basierend auf der Ähnlichkeit von Höhe, Fläche und Geschosszahl. Höhere Werte zeigen einheitliche Gebäudeeigenschaften an.',
            description: 'Misst die architektonische Ähnlichkeit',
            title: 'Physische Kohäsion',
          },
        },
        temporal: {
          label: 'Zeitliche Kohäsion (40%)',
          source: 'Popety.io, GWR',
          tooltip: {
            calculation: 'Basierend auf der Ähnlichkeit der Baujahre. Höhere Werte zeigen Gebäude aus derselben Epoche an.',
            description: 'Misst, ob Gebäude in derselben Periode gebaut wurden',
            title: 'Zeitliche Kohäsion',
          },
        },
        typological: {
          label: 'Typologische Kohäsion (20%)',
          source: 'Popety.io, GWR',
          tooltip: {
            calculation: 'Basierend auf der Häufigkeit ähnlicher Gebäudekategorien (z.B. Einfamilienhaus, Mehrfamilienhaus, etc.)',
            description: 'Misst die Ähnlichkeit der Gebäudetypen',
            title: 'Typologische Kohäsion',
          },
        },
      },
      comparison: {
        aboveAverage: 'Überdurchschnittlich',
        belowAverage: 'Unterdurchschnittlich',
        similar: 'Ähnlich',
      },
      conformity: {
        fitsWell: 'Passt gut',
        outlier: 'Ausreisser',
        partialFit: 'Teilweise passend',
        physicalMatch: {
          label: 'Physische Übereinstimmung (40%)',
          source: 'Popety.io, swissSURFACE3D',
          tooltip: {
            calculation: 'Durchschnitt der Ähnlichkeit von Höhe, Fläche und Geschosszahl. ±20-30% = hohe Übereinstimmung',
            description: 'Wie gut Grösse und Höhe dieses Gebäudes zum typischen Quartier passen',
            title: 'Physische Übereinstimmung',
          },
        },
        temporalMatch: {
          label: 'Zeitliche Übereinstimmung (40%)',
          source: 'Popety.io, GWR',
          tooltip: {
            calculation: 'Vergleicht Baujahr mit typischem Jahr. ±5 Jahre = 100%, ±10 Jahre = 75%, ±20 Jahre = 50%',
            description: 'Wie gut das Baujahr dieses Gebäudes zur typischen Quartierperiode passt',
            title: 'Zeitliche Übereinstimmung',
          },
        },
        tooltip: {
          source: 'Popety.io',
        },
        typologicalMatch: {
          label: 'Typologische Übereinstimmung (20%)',
          source: 'Popety.io, GWR',
          tooltip: {
            calculation: '100 wenn gleiche Kategorie wie typisch, 0 wenn unterschiedlich',
            description: 'Ob der Typ dieses Gebäudes zur dominanten Quartierkategorie passt',
            title: 'Typologische Übereinstimmung',
          },
        },
      },
      errors: {
        loadFailed: 'Laden der Nachbarschaftsdaten fehlgeschlagen',
      },
      loading: 'Nachbarschaftsdaten werden geladen...',
      metrics: {
        areaVsAverage: {
          label: 'Fläche vs Durchschnitt',
          tooltip: {
            calculation: 'Prozentuale Abweichung von der durchschnittlichen Fläche der Gebäude im Suchradius',
            description: 'Wie sich die Grundfläche dieses Gebäudes mit den nahegelegenen Gebäuden vergleicht',
            importance: 'Zeigt an, ob das Gebäude typisch oder ungewöhnlich gross/klein für das Gebiet ist. Beeinflusst GFZ und Entwicklungspotenzial.',
            source: 'Popety.io, GWR',
            title: 'Fläche vs Nachbarschaftsdurchschnitt',
          },
        },
        avgNeighborAge: {
          label: 'Durchschnittliches Nachbaralter',
          tooltip: {
            calculation: 'Durchschnitt der Alter aller Gebäude im Suchradius',
            description: 'Durchschnittsalter der umliegenden Gebäude',
            importance: 'Zeigt an, ob die Nachbarschaft vorwiegend alt (>50 Jahre), etabliert (20-50 Jahre) oder neu (<20 Jahre) ist.',
            source: 'Popety.io, GWR',
            title: 'Durchschnittliches Nachbaralter',
          },
          value: '{{years}} Jahre',
        },
        buildingConformity: 'Gebäudekonformität',
        buildingDensity: {
          label: 'Gebäudedichte',
          tooltip: {
            calculation: 'Anzahl der Gebäude innerhalb eines definierten Radius geteilt durch die Fläche dieses Radius',
            description: 'Anzahl der Gebäude pro Flächeneinheit in der Umgebung',
            importance: 'Zeigt an, ob das Gebiet urban, suburban oder ländlich ist. Hohe Dichte deutet auf städtische Umgebung mit näheren Gebäuden hin.',
            source: 'Popety.io, GWR',
            title: 'Gebäudedichte',
          },
          value: '{{density}} Gebäude/km²',
        },
        buildingsAnalyzed: 'Analysierte Gebäude',
        confidence: 'Vertrauen',
        heightVsAverage: {
          label: 'Höhe vs Durchschnitt',
          tooltip: {
            calculation: 'Prozentuale Abweichung von der durchschnittlichen Höhe der Gebäude im Suchradius',
            description: 'Wie sich die Höhe dieses Gebäudes mit den nahegelegenen Gebäuden vergleicht',
            importance: 'Gebäude, die deutlich höher oder niedriger als Nachbarn sind, können Zonenbeschränkungen unterliegen oder visuell auffallen.',
            source: 'Popety.io, GWR',
            title: 'Höhe vs Nachbarschaftsdurchschnitt',
          },
        },
        neighborhoodCohesion: 'Nachbarschaftskohäsion',
        neighboringBuildings: {
          label: 'Nachbargebäude',
          tooltip: {
            calculation: 'Anzahl der Gebäude, die innerhalb des ausgewählten Suchradius gefunden wurden',
            description: 'Anzahl der Gebäude innerhalb des Analyseradius',
            importance: 'Mehr Nachbarn ermöglichen einen besseren statistischen Vergleich. Weniger als 10 Nachbarn können auf ein ländliches Gebiet oder begrenzte Datenzuverlässigkeit hinweisen.',
            source: 'Popety.io, GWR',
            title: 'Nachbargebäude',
          },
        },
        volumeVsAverage: {
          label: 'Volumen vs Durchschnitt',
          tooltip: {
            calculation: 'Prozentuale Abweichung vom durchschnittlichen Volumen der Gebäude im Suchradius',
            description: 'Wie sich das Volumen dieses Gebäudes mit den nahegelegenen Gebäuden vergleicht',
            importance: 'Grössere Volumen weisen auf substanziellere Strukturen hin. Beeinflusst Heizkosten und Entwicklungsdichte.',
            source: 'Popety.io, GWR',
            title: 'Volumen vs Nachbarschaftsdurchschnitt',
          },
        },
      },
      neighboringBuildings: 'Nachbargebäude',
      noData: 'Keine Nachbarschaftsdaten verfügbar',
      notes: {
        differsByPattern: 'Weicht vom Nachbarschaftsmuster ab',
        physicalMismatch: 'Physische Merkmale weichen vom Nachbarschaftsdurchschnitt ab',
        temporalMismatch: 'Gebäudealter weicht vom Nachbarschaftsmuster ab',
        title: 'Notizen',
        typologicalMismatch: 'Gebäudetyp weicht vom Nachbarschaftsstandard ab',
      },
      profile: {
        description: {
          label: 'Beschreibung',
          source: 'Popety.io, GWR',
          tooltip: {
            calculation: 'Generiert aus typischem Baujahr und dominanter Kategorie',
            description: 'Zusammenfassende Charakterisierung der Nachbarschaft',
            importance: "Bietet schnellen Überblick über den Nachbarschaftscharakter (z.B. 'Wohngebiet der 1960er' oder 'gemischt genutzt der 1980er').",
            title: 'Quartierbeschreibung',
          },
        },
        developmentCategories: {
          source: 'Popety.io, GWR',
        },
        developmentPattern: {
          categories: {
            mixed: {
              description: 'Moderate Kohäsion, verschiedene Entwicklung (Score 55-74)',
              label: 'Gemischt',
            },
            organic: {
              description: 'Niedrige Kohäsion, vielfältige Entwicklung (Score <55)',
              label: 'Organisch',
            },
            planned: {
              description: 'Hohe Kohäsion, einheitliche Entwicklung (Score ≥75)',
              label: 'Geplant',
            },
          },
          label: 'Entwicklungsmuster',
          source: 'Popety.io, GWR',
          tooltip: {
            description: 'Klassifizierung des Nachbarschaftsentwicklungsstils',
            importance: 'Geplante Gebiete haben strengere Zonierung. Organische Gebiete bieten mehr Renovierungsflexibilität.',
            title: 'Entwicklungsmuster',
          },
        },
        typicalArea: {
          label: 'Typische Fläche',
          source: 'Popety.io',
          tooltip: {
            calculation: 'Durchschnittliche Grundfläche der Gebäude in der Nachbarschaft',
            description: 'Repräsentative Gebäudegrundfläche für das Quartier',
            importance: 'Zeigt typische Grundstücksauslastung und Gebäudemassstab im Gebiet.',
            title: 'Typische Fläche',
          },
        },
        typicalCategory: {
          label: 'Typische Kategorie',
          source: 'Popety.io, GWR',
          tooltip: {
            calculation: 'Häufigste Gebäudekategorie unter den Nachbarschaftsgebäuden',
            description: 'Dominierender Gebäudetyp im Quartier',
            importance: 'Definiert Nachbarschaftscharakter: Wohn-, Gewerbe-, Industrie- oder Mischnutzung.',
            title: 'Typische Kategorie',
          },
        },
        typicalHeight: {
          label: 'Typische Höhe',
          source: 'Popety.io, swissSURFACE3D',
          tooltip: {
            calculation: 'Durchschnittliche Höhe der Gebäude in der Nachbarschaft',
            description: 'Repräsentative Gebäudehöhe für das Quartier',
            importance: 'Zeigt an, ob das Gebiet vorwiegend niedrige (<10m), mittlere (10-25m) oder hohe (>25m) Bebauung hat.',
            title: 'Typische Höhe',
          },
        },
        typicalYear: {
          label: 'Typisches Baujahr',
          source: 'Popety.io, GWR',
          tooltip: {
            calculation: 'Durchschnittliches Baujahr der Gebäude in der Nachbarschaft',
            description: 'Repräsentativstes Baujahr für das Quartier',
            importance: 'Definiert die architektonische Epoche und wahrscheinliche Baustandards für das Gebiet.',
            title: 'Typisches Baujahr',
          },
        },
      },
      radius: {
        label: 'Analyseradius',
        value: '{{value}}m',
      },
      statistics: {
        area: {
          mean: {
            source: 'Popety.io, GWR',
            tooltip: {
              calculation: 'Arithmetisches Mittel aller Gebäudeflächen',
              description: 'Durchschnittliche Gebäudegrundfläche in der Nachbarschaft',
              importance: 'Zeigt typische Grundstücksgrösse. Beeinflusst durch Ausreisser.',
              title: 'Durchschnittliche Fläche',
            },
          },
          median: {
            source: 'Popety.io, GWR',
            tooltip: {
              calculation: '50. Perzentil der Gebäudeflächen',
              description: 'Mittelwert der Nachbarschafts-Gebäudeflächen',
              importance: 'Repräsentativer als Mittelwert bei Ausreissern.',
              title: 'Medianfläche',
            },
          },
          range: {
            source: 'Popety.io, GWR',
            tooltip: {
              calculation: 'Kleinste und grösste Gebäudegrundflächen in der Nachbarschaft',
              description: 'Minimale und maximale Gebäudeflächen',
              importance: 'Zeigt Grössendiversität. Grosse Spanne zeigt gemischte Gebäudemassstäbe.',
              title: 'Flächenbereich',
            },
          },
          title: 'Fläche',
        },
        floor: {
          floorsRange: '{{min}} - {{max}} Stockwerke',
          floorsValue: '{{floors}} Stockwerke',
          mean: {
            source: 'Popety.io, GWR',
            tooltip: {
              calculation: 'Arithmetisches Mittel aller Gebäudegeschosszahlen',
              description: 'Durchschnittliche Anzahl von Geschossen in der Nachbarschaft',
              importance: 'Zeigt typischen Gebäudemassstab. Beeinflusst durch Ausreisser.',
              title: 'Durchschnittliche Geschosszahl',
            },
          },
          median: {
            source: 'Popety.io, GWR',
            tooltip: {
              calculation: '50. Perzentil der Geschosszahlen',
              description: 'Mittelwert der Nachbarschafts-Geschosszahlen',
              importance: 'Repräsentativer als Mittelwert bei Ausreissern.',
              title: 'Median-Geschosszahl',
            },
          },
          range: {
            source: 'Popety.io, GWR',
            tooltip: {
              calculation: 'Niedrigste und höchste Geschosszahlen in der Nachbarschaft',
              description: 'Minimale und maximale Geschosszahlen',
              importance: 'Zeigt vertikale Diversität. Grosse Spanne zeigt gemischte Höhenniveaus.',
              title: 'Geschosszahlbereich',
            },
          },
          title: 'Stockwerke',
        },
        height: {
          mean: {
            source: 'Popety.io, swissSURFACE3D',
            tooltip: {
              calculation: 'Arithmetisches Mittel aller Gebäudehöhen',
              description: 'Durchschnittliche Gebäudehöhe in der Nachbarschaft',
              importance: 'Zeigt typischen Gebäudemassstab. Beeinflusst durch Ausreisser.',
              title: 'Durchschnittliche Höhe',
            },
          },
          median: {
            source: 'Popety.io, swissSURFACE3D',
            tooltip: {
              calculation: '50. Perzentil der Gebäudehöhen',
              description: 'Mittelwert der Nachbarschafts-Gebäudehöhen',
              importance: 'Repräsentativer als Mittelwert bei Ausreissern.',
              title: 'Medianhöhe',
            },
          },
          range: {
            source: 'Popety.io, swissSURFACE3D',
            tooltip: {
              calculation: 'Niedrigste und höchste Gebäudehöhen in der Nachbarschaft',
              description: 'Minimale und maximale Gebäudehöhen',
              importance: 'Zeigt Höhendiversität. Grosse Spanne zeigt gemischte Entwicklung.',
              title: 'Höhenbereich',
            },
          },
          title: 'Höhe',
        },
        mean: 'Mittelwert',
        median: 'Median',
        range: 'Bereich',
      },
      title: 'Nachbarschaftskontext',
      tooltips: {
        source: 'Datenquelle',
        sourceCalculated: 'Berechnet aus Gebäuderegisterdaten',
      },
      warnings: {
        outlierDescription: 'Dieses Gebäude passt nicht zum kohärenten Nachbarschaftsmuster. Es kann in Bezug auf Alter, Grösse oder Typ ein Ausreisser im Vergleich zu umliegenden Gebäuden sein.',
        outlierTitle: 'Dieses Gebäude ist ein Ausreisser',
      },
    },
    overview: {
      ageCategories: {
        contemporary: {
          description: 'Mit relativ modernen Bauverfahren und Materialien errichtet',
          label: 'Zeitgenössisch',
          range: '10-30 Jahre',
        },
        historic: {
          description: 'Historisches Gebäude mit potenziellem Denkmalwert und Charaktereigenschaften',
          label: 'Historisch',
          range: '>50 Jahre',
        },
        mature: {
          description: 'Etabliertes Gebäude, das von Renovierung oder Modernisierung profitieren könnte',
          label: 'Ausgereift',
          range: '30-50 Jahre',
        },
        new: {
          description: 'Kürzlich mit modernen Baustandards und Energieeffizienz errichtet',
          label: 'Neu',
          range: '<10 Jahre',
        },
      },
      cards: {
        dimensions: {
          buildingVolume: 'Gebäudevolumen',
          footprintArea: 'Grundfläche',
          grossFloorArea: 'Bruttogeschossfläche',
          groundElevation: 'Bodenhöhe',
          title: 'Physische Dimensionen',
          tooltips: {
            elevation: {
              calculation: 'Gemessen aus LiDAR-Daten',
              description: 'Höhe des Bodenniveaus am Gebäudestandort über dem Meeresspiegel',
              importance: 'Wichtig für Hochwasserrisikobewertung, Entwässerungsplanung und Verständnis der lokalen Topographie',
              source: 'Popety.io, swissSURFACE3D',
              title: 'Bodenhöhe',
            },
            footprintImportance: 'Bestimmt die Bodenbedeckung und ist entscheidend für die Berechnung des Entwicklungspotenzials und der Einhaltung von Zonenvorschriften',
            grossFloorArea: {
              calculation: 'Summe aller Geschossflächen einschliesslich Wände, Treppen und Gemeinschaftsflächen. Berechnet aus dem Gebäuderegister oder Grundfläche × Geschosszahl',
              description: 'Gesamtgeschossfläche aller Stockwerke, gemessen bis zur Aussenseite der Aussenwände',
              importance: 'Schlüsselkennzahl zur Bewertung des Entwicklungspotenzials, insbesondere der IBUS-Berechnung',
              limitations: 'Kann nicht bewohnbare Bereiche wie Flure und Technikräume einschliessen. Die Nettonutzfläche ist typischerweise 10-25% kleiner',
              source: 'GWR, Popety.io',
              title: 'Bruttogeschossfläche',
            },
            heightCalculation: 'Höhe gemessen vom Bodenniveau bis zum Dachfirst mittels LiDAR-Daten und offiziellen Gebäudevermessungen',
            heightImportance: 'Entscheidend für Zonenkonformität, Nachbarschaftsbeziehungen und Gebäudeklassifizierung',
            volume: {
              calculation: 'Gemessen aus LiDAR-Daten',
              description: 'Gesamtes umschlossenes Volumen des Gebäudes von den Fundamenten bis zum Dach',
              importance: 'Verwendet für Energiemodellierung, HLK-Dimensionierung und Entwicklungsdichteberechnungen, insbesondere für den IM (Massenindex)',
              source: 'Popety.io, swissSURFACE3D',
              title: 'Gebäudevolumen',
            },
          },
        },
        occupancy: {
          avgFloorArea: 'Durchschn. Geschossfläche',
          avgFloorHeight: 'Durchschn. Geschosshöhe',
          buildingCategory: 'Gebäudekategorie',
          numberOfDwellings: 'Anzahl Wohnungen',
          popetyClassification: 'Popety.io-Klassifikation',
          primaryUse: 'Hauptnutzung',
          title: 'Belegung & Typ',
          tooltips: {
            avgFloorArea: {
              calculation: 'Berechnet durch Division der Bruttogeschossfläche durch die Anzahl der Stockwerke',
              description: 'Die durchschnittliche Fläche jedes Stockwerks im Gebäude',
              importance: 'Zeigt die durchschnittliche Nutzfläche eines Geschosses',
              source: 'Popety.io',
              title: 'Durchschnittliche Geschossfläche',
            },
            avgFloorHeight: {
              calculation: 'Berechnet durch Division der Gesamtgebäudehöhe durch die Anzahl der Stockwerke',
              description: 'Die durchschnittliche vertikale Distanz zwischen den Stockwerken im Gebäude',
              importance: 'Zeigt die typische Geschosshöhe und den vertikalen Komfort',
              source: 'Popety.io',
              title: 'Durchschnittliche Geschosshöhe',
            },
            category: {
              calculation: 'Vom eidgenössischen GWR basierend auf ihrer dominanten Nutzung zugewiesen',
              description: 'Unterteilung der Gebäude nach ihrer Nutzung',
              importance: 'Ordnet das Gebäude nach der amtlichen Nutzung ein, damit es mit ähnlichen Gebäuden verglichen werden kann',
              source: 'GWR',
              title: 'Gebäudekategorie',
            },
            dwellings: {
              calculation: 'Gezählt aus offiziellen Gebäuderegistern',
              description: 'Gesamtzahl der einzelnen Wohneinheiten im Gebäude',
              importance: 'Zählt die Wohnungen im Gebäude und stützt Belegung und Schätzung',
              source: 'GWR',
              title: 'Anzahl Wohnungen',
            },
            popetyClassification: {
              calculation: 'Von Popety.io basierend auf Gebäudedaten, Nutzungsmustern und Marktanalyse berechnet',
              description: 'Gebäudeklassifikation durch den proprietären Algorithmus von Popety.io',
              importance: 'Bietet eine standardisierte Klassifikation für Vergleichs- und Bewertungszwecke',
              title: 'Popety.io-Klassifikation',
            },
            primaryUse: {
              calculation: 'Die aktuelle EUROSTAT*-Klassifikation unterteilt alle Bauwerke in 2 Sektionen (Gebäude / Tiefbau), 6 Abteilungen (2/4), 20 Gruppen (10/10) und 46 Klassen (21/25).',
              description: 'Aufschlüsselung der Gebäude nach der EUROSTAT+-Klassifikation',
              importance: 'Bestimmt die Hauptnutzung, die Zonierung, Schätzung und Vergleichsobjekte prägt',
              source: 'GWR',
              title: 'Gebäudeklasse',
            },
          },
        },
        occupancyType: {
          title: 'Nutzung & Typ',
        },
        physicalDimensions: {
          title: 'Physische Masse',
        },
        status: {
          constructionYear: 'Baujahr',
          currentStatus: 'Aktueller Status',
          lastRenovation: 'Letzte Renovierung',
          minergieCertified: 'Minergie-zertifiziert',
          protectedStatus: 'Schutzstatus',
          title: 'Status & Zustand',
          tooltips: {
            constructionYear: {
              calculation: 'Übernommen aus dem Baujahr des eidgenössischen Gebäude- und Wohnungsregisters (GWR)',
              description: 'Das Jahr, in dem das Gebäude ursprünglich fertiggestellt wurde',
              importance: 'Datiert das Gebäude und ist die Grundlage für Alter, Zustand und Erneuerungen',
              limitations: 'Bei älteren Gebäuden kann aus dem Bauperiodebereich geschätzt werden. Grössere Umbauten aktualisieren dieses Datum möglicherweise nicht',
              source: 'GWR',
              title: 'Baujahr',
            },
            minergie: {
              calculation: 'Von der Minergie-Vereinigung basierend auf der Erfüllung strenger Energieeffizienzstandards verliehen',
              description: 'Schweizer Energieeffizienz-Zertifizierung für Gebäude',
              importance: 'Zeigt überlegene Energieeffizienz, höheren Komfort und typischerweise niedrigere Betriebskosten an',
              source: 'Bundesamt für Energie BFE',
              title: 'Minergie-Zertifizierung',
            },
            protected: {
              calculation: 'Von kantonalen Denkmalschutzbehörden basierend auf historischer, architektonischer oder kultureller Bedeutung bewertet',
              description: 'Von kantonalen Behörden zugewiesener Denkmalschutzstufe',
              importance: 'Beeinflusst Renovierungsmöglichkeiten und Entwicklungspotenzial',
              source: 'Kantonales Denkmalschutzamt',
              title: 'Schutzstatus',
            },
            renovation: {
              calculation: 'Übernommen aus dem Jahr der letzten wertvermehrenden Erneuerung im Gebäuderegister',
              description: 'Jahr der letzten Renovierung, die den Gebäudewert wesentlich gesteigert hat.',
              importance: 'Zeigt, ob das Gebäude seit dem Bau erneuert wurde',
              limitations: 'SEIT 2017 NICHT MEHR IM GWR AKTUALISIERT.',
              source: 'GWR',
              title: 'Letzte Renovation',
            },
            status: {
              calculation: 'Im Gebäuderegister erfasst und basierend auf Bau- oder Abrissgenehmigungen aktualisiert',
              description: 'Der aktuelle Betriebsstatus des Gebäudes',
              importance: 'Bestätigt, ob das Gebäude existiert, im Bau ist oder abgerissen wurde',
              source: 'GWR',
              title: 'Aktueller Gebäudestatus',
            },
          },
        },
        statusCondition: {
          title: 'Status & Zustand',
        },
      },
      heightTypes: {
        highRise: 'Hoch',
        lowRise: 'Niedrig',
        midRise: 'Mittel',
      },
      labels: {
        asl: 'm ü.M.',
        estimated: 'Geschätzt',
        period: 'Zeitraum',
      },
      metrics: {
        buildingAge: {
          label: 'Alter',
          tooltip: {
            calculation: 'Berechnet als aktuelles Jahr minus Baujahr aus dem offiziellen Gebäuderegister',
            calculationEstimated: 'Geschätzt basierend auf der Bauperiode {{period}}. Der Mittelpunkt des Periodenbereichs wird für die Berechnung verwendet',
            description: 'Die Anzahl der Jahre seit der ursprünglichen Errichtung des Gebäudes',
            importance: 'Schlüsselfaktor für die Bewertung von Wartungsbedarf, Renovierungspotenzial, Energieeffizienz und Immobilienbewertung',
            limitations: 'Berücksichtigt keine grösseren Renovierungen oder Umbauten. Kann geschätzt sein, wenn das genaue Baujahr unbekannt ist oder eine Periode (z.B.: 1946-1960)',
            limitationsEstimated: 'Das Alter ist aus der Bauperiode geschätzt und möglicherweise nicht exakt. Das tatsächliche Baujahr kann innerhalb des Periodenbereichs variieren',
            source: 'GWR, Popety.io',
            title: 'Gebäudealter',
          },
        },
        buildingArea: {
          label: 'Fläche',
          tooltip: {
            calculation: 'Gemessen aus offiziellen Katasterdaten oder dem Gebäuderegister als horizontale Projektion der Gebäudehülle',
            description: 'Die Grundfläche des Gebäudes',
            importance: 'Wesentlich für das Verständnis der Landnutzungseffizienz, Berechnung von Geschossflächenzahlen und Bewertung des Entwicklungspotenzials',
            limitations: 'Beinhaltet keine externen Strukturen wie Terrassen, Balkone oder Untergeschosse. Kann von der Gesamtgeschossfläche abweichen',
            source: 'Popety.io, GWR',
            title: 'Gebäudegrundfläche',
          },
        },
        buildingHeight: {
          label: 'Höhe',
          tooltip: {
            calculation: 'Gemessen aus LiDAR-Daten oder offiziellen Gebäudevermessungen, vom Bodenniveau bis zum Dachfirst oder höchsten Strukturelement',
            description: 'Die vertikale Messung vom Bodenniveau bis zum höchsten Punkt des Gebäudes',
            importance: 'Kritisch für Zonenkonformität und Bewertung des Entwicklungs- oder Aufstockungspotenzials',
            limitations: 'Kann je nach Messmethode variieren. Antennen, Schornsteine und temporäre Strukturen können eingeschlossen sein oder nicht',
            source: 'Popety.io, swissSURFACE3D',
            title: 'Gesamte Gebäudehöhe',
          },
        },
        floorCount: {
          label: 'Stockwerke',
          tooltip: {
            calculation: 'Gezählt aus offiziellen Gebäuderegisterdaten, Dachgeschosse und Kellergeschosse werden nur gezählt, wenn sie ausgebaut, auch teilweise, für Wohnzwecke oder beheizt sind',
            description: 'Die Gesamtzahl der Stockwerke und Untergeschosse des Gebäudes, einschliesslich des Erdgeschosses',
            importance: 'Wichtiger Indikator für Gebäudeklassifizierung, Zonenkonformität und Schätzung der Bruttogeschossfläche',
            limitations: 'Kann Kellergeschosse, Zwischengeschosse oder Technikgeschosse nicht einschliessen, Keller werden nicht berücksichtigt.',
            source: 'GWR',
            title: 'Gesamte Stockwerkzahl',
          },
        },
        numberOfFloors: {
          label: 'Anzahl Stockwerke',
        },
      },
      sizeIndicators: {
        compact: 'Kompakt',
        large: 'Gross',
        medium: 'Mittel',
      },
      values: {
        asl: 'ü.M.',
        estimated: '(gesch.)',
        estimatedFrom: 'Geschätzt aus {{source}}',
        na: 'K.A.',
        no: 'Nein',
        period: '(Zeitraum)',
        years: 'Jahre',
        yearsAgo: 'Vor {{count}} Jahren',
        yearsCount: '{{count}} Jahre',
        yes: 'Ja',
      },
    },
    states: {
      error: 'Fehler: {{error}}',
      loading: 'Laden...',
      noData: 'Keine Daten verfügbar',
    },
    tabs: {
      neighborhood: 'Nachbarschaft',
      overview: 'Übersicht',
      timeline: 'Zeitachse',
    },
    timeline: {
      ageComparison: {
        comparisonTypes: {
          newerDescription: 'Dieses Gebäude wurde neuer als der Nachbarschaftsdurchschnitt gebaut, wahrscheinlich mit moderneren Standards',
          newerThanAverage: 'Neuer als Durchschnitt',
          olderDescription: 'Dieses Gebäude ist deutlich älter als der Nachbarschaftsdurchschnitt, was Renovierungsbedarf und Energieeffizienz beeinflussen kann',
          significantlyOlder: 'Deutlich älter',
          similarAge: 'Ähnliches Alter',
          similarDescription: 'Dieses Gebäude wurde ungefähr zur gleichen Zeit wie die meisten Gebäude in der Nachbarschaft gebaut',
        },
        labels: {
          comparison: 'Vergleich',
          neighborhoodAverage: 'Nachbarschaftsdurchschnitt',
          thisBuilding: 'Dieses Gebäude',
        },
        newerThanAverage: 'Neuer als Durchschnitt',
        significantlyOlder: 'Deutlich älter',
        similarAge: 'Ähnliches Alter',
        title: 'Altersvergleich',
        tooltip: {
          calculation: 'Basierend auf dem Baujahr dieses Gebäudes im Vergleich zum durchschnittlichen Baujahr der Nachbargebäude',
          description: 'Vergleicht das Alter dieses Gebäudes mit dem Durchschnittsalter der Gebäude in der umliegenden Nachbarschaft',
          importance: 'Das Verständnis, wie sich das Gebäudealter im Vergleich zur Nachbarschaft verhält, hilft bei der Bewertung des relativen Zustands und des Entwicklungskontexts',
          limitations: 'Der Vergleich basiert auf Gebäuden innerhalb des Analyseradius. Gebiete mit gemischter Bebauung können eine hohe Varianz aufweisen',
          source: 'GWR, Popety.io',
          title: 'Altersvergleich mit der Nachbarschaft',
        },
        tooltips: {
          comparison: {
            description: 'Wie sich das Alter dieses Gebäudes mit dem Nachbarschaftsdurchschnitt vergleicht',
            importance: 'Neuere Gebäude können eine bessere Energieeffizienz aufweisen; ältere haben möglicherweise Charakterwert oder benötigen mehr Wartung',
            source: 'Berechnet aus Gebäuderegisterdaten',
            title: 'Ergebnis des Altersvergleichs',
          },
          neighborhoodAverage: {
            calculation: 'Mittleres Baujahr aller Gebäude innerhalb des Analyseradius',
            description: 'Das durchschnittliche Baujahr und Alter der Gebäude in der umliegenden Nachbarschaft',
            importance: 'Zeigt die typische Bauepoche der Nachbarschaft und bietet Kontext für das analysierte Gebäude',
            source: 'GWR, Popety.io',
            title: 'Nachbarschaftsdurchschnitt',
          },
          thisBuilding: {
            calculation: 'Baujahr aus dem offiziellen Gebäuderegister; Alter berechnet als aktuelles Jahr minus Baujahr',
            description: 'Das Baujahr und Alter des analysierten Gebäudes',
            importance: 'Bezugspunkt für den Altersvergleich der Nachbarschaft',
            source: 'GWR',
            title: 'Dieses Gebäude',
          },
        },
        yearsOld: 'Jahre alt',
      },
      badges: {
        estimatedData: 'Geschätzte Daten',
        historicalData: 'Historische Daten',
      },
      cards: {
        ageAnalysis: {
          title: 'Altersanalyse',
          tooltips: {
            ageCategory: {
              description: 'Klassifizierung des Gebäudes anhand seines Alters in standardisierte Kategorien',
              importance: 'Ordnet das Gebäude einer Altersklasse zu, die für Zustand und Marktvergleich verwendet wird',
              source: 'GWR, Popety.io',
              title: 'Alterskategorie',
            },
            buildingAge: {
              calculation: 'Berechnet als aktuelles Jahr minus Baujahr aus dem offiziellen Gebäuderegister',
              description: 'Die Anzahl der Jahre seit der ursprünglichen Errichtung des Gebäudes',
              importance: 'Schlüsselfaktor für die Bewertung von Wartungsbedarf, Renovierungspotenzial, Energieeffizienz und Immobilienbewertung',
              limitations: 'Berücksichtigt keine grösseren Renovierungen oder Umbauten. Kann geschätzt sein, wenn das genaue Baujahr unbekannt ist oder eine Periode (z.B.: 1946-1960)',
              source: 'GWR, Popety.io',
              title: 'Gebäudealter',
            },
            buildingPeriod: {
              calculation: 'Abgeleitet aus dem Gebäuderegister, wenn kein genaues Baujahr verfügbar ist',
              description: 'Die Bauepoche oder der Zeitraum, in dem das Gebäude errichtet wurde',
              importance: 'Bietet Kontext über Baustandards und typische Materialien dieser Epoche',
              title: 'Bauperiode',
            },
            constructionPeriod: {
              calculation: 'Abgeleitet vom Baujahr und den üblichen Bauperioden',
              source: 'GWR',
            },
            constructionYear: {
              calculation: 'Übernommen aus dem amtlichen Baujahr im Gebäuderegister',
              importance: 'Verankert die Bauzeitachse und das Alter des Gebäudes',
              limitations: 'Bei älteren Gebäuden kann aus dem Bauperiodebereich geschätzt werden. Grössere Umbauten aktualisieren dieses Datum möglicherweise nicht',
              source: 'GWR',
            },
          },
        },
        ageComparison: {
          title: 'Altersvergleich mit der Nachbarschaft',
        },
        constructionPhases: {
          title: 'Bauphasen',
          tooltips: {
            constructionDates: {
              calculation: 'Aus den Baugeschichtsaufzeichnungen des Gebäuderegisters extrahiert',
              description: 'Die spezifischen Jahre, in denen Bau- oder Erweiterungsphasen abgeschlossen wurden',
              importance: 'Zeigt, wann grössere Änderungen stattfanden, und hilft, die Gebäudeentwicklung und gemischte Baustandards zu verstehen',
              source: 'GWR',
              title: 'Baudaten',
            },
            currentStatus: {
              importance: 'Bestätigt, ob das Gebäude im Kontext seiner Baugeschichte betriebsbereit, im Bau oder abgerissen ist',
              source: 'GWR',
            },
            numberOfPhases: {
              calculation: 'Anzahl der im Gebäuderegister erfassten unterschiedlichen Baudaten',
              description: 'Wie viele unterschiedliche Bau- oder Erweiterungsphasen das Gebäude durchlaufen hat',
              importance: 'Mehrere Phasen können auf Erweiterungen oder Anbauten hinweisen, die Gebäudestruktur und -wert beeinflussen',
              source: 'GWR',
              title: 'Anzahl der Bauphasen',
            },
            singlePhase: {
              calculation: 'Keine zusätzlichen Baudaten im Gebäuderegister gefunden',
              description: 'Das Gebäude wurde in einer einzigen Phase ohne nachfolgende Erweiterungen errichtet',
              importance: 'Einphasige Gebäude haben typischerweise einheitlichere Baustandards',
              source: 'GWR',
            },
          },
        },
        constructionTimeline: {
          title: 'Bau-Zeitachse',
        },
        renovationHistory: {
          title: 'Renovierungsgeschichte',
          tooltips: {
            lastRenovation: {
              calculation: 'Übernommen aus dem Jahr der letzten wertvermehrenden Erneuerung im Gebäuderegister',
              description: 'Jahr der letzten Renovierung, die den Gebäudewert wesentlich gesteigert hat',
              importance: 'Zeigt, wie lange die letzte wertvermehrende Erneuerung zurückliegt',
              limitations: 'SEIT 2017 NICHT MEHR IM GWR AKTUALISIERT.',
              source: 'GWR',
            },
            period: {
              calculation: 'Abgeleitet aus Renovierungsgenehmigungsaufzeichnungen im Gebäuderegister',
              description: 'Die Epoche oder der Zeitraum, in dem die Renovierung abgeschlossen wurde',
              importance: 'Bietet Kontext über Art und Qualität der durchgeführten Renovierungsarbeiten',
              title: 'Renovierungsperiode',
            },
            renovationPeriod: {
              calculation: 'Abgeleitet vom Jahr der letzten Erneuerung und den üblichen Erneuerungsperioden',
              importance: 'Ordnet die Erneuerung einer Periode zu, um Arbeiten derselben Zeit zu vergleichen',
              limitations: 'SEIT 2017 NICHT MEHR IM GWR AKTUALISIERT.',
              source: 'GWR',
            },
            renovationStatus: {
              limitations: 'SEIT 2017 NICHT MEHR IM GWR AKTUALISIERT.',
              source: 'Popety.io',
            },
            status: {
              description: 'Bewertung des Renovierungsbedarfs des Gebäudes basierend auf der Zeit seit der letzten Renovierung',
              importance: 'Schneller Indikator, ob eine Renovierung in naher Zukunft geplant werden sollte',
              title: 'Renovierungsstatus',
            },
            yearsSince: {
              calculation: 'Aktuelles Jahr minus letztes Renovierungsjahr aus dem Gebäuderegister',
              description: 'Die Anzahl der Jahre seit der letzten Renovierung, die den Gebäudewert wesentlich gesteigert hat',
              importance: 'Hebt Gebäude hervor, deren letzte wertvermehrende Erneuerung nicht mehr aktuell ist',
              limitations: 'SEIT 2017 NICHT MEHR IM GWR AKTUALISIERT.',
              source: 'GWR',
              title: 'Jahre seit Renovierung',
            },
          },
        },
        timeline: {
          title: 'Bauzeitachse',
          tooltip: {
            calculation: 'Basierend auf offiziellen Baudaten, Renovierungsaufzeichnungen und Gebäuderegisterdaten',
            description: 'Visuelle Darstellung der Baugeschichte und wichtiger Ereignisse des Gebäudes',
            importance: 'Fasst Bau- und Erneuerungsmeilensteine auf einer Zeitachse zusammen',
            limitations: 'Einige Daten können aus Bauperioden geschätzt werden, wenn genaue Jahre nicht verfügbar sind, und einige Ereignisse können fehlen',
            source: 'GWR, Popety.io',
            title: 'Bau-Zeitachse',
          },
        },
      },
      chart: {
        tooltipAge: 'Alter',
        tooltipYear: 'Jahr',
        xAxisLabel: 'Jahr',
        yAxisLabel: 'Gebäude',
      },
      events: {
        constructionPhase: 'Bauphase',
        dwellingAdded: 'Wohnung hinzugefügt',
        majorRenovation: 'Grössere Renovierung',
        originalConstruction: 'Ursprüngliche Konstruktion',
        originalConstructionEst: 'Ursprüngliche Konstruktion (Gesch.)',
        present: 'Gegenwart',
      },
      labels: {
        ageCategory: 'Alterskategorie',
        buildingAge: 'Gebäudealter',
        buildingPeriod: 'Bauperiode',
        comparison: 'Vergleich',
        constructionDates: 'Baudaten',
        constructionYear: 'Baujahr',
        lastRenovation: 'Letzte Renovierung',
        neighborhoodAverage: 'Nachbarschaftsdurchschnitt',
        noRecord: 'Keine Aufzeichnung',
        numberOfPhases: 'Anzahl Phasen',
        phases: 'Phasen',
        renovationPeriod: 'Renovierungsperiode',
        renovationStatus: 'Renovierungsstatus',
        singlePhaseConstruction: 'Einphasige Konstruktion',
        thisBuilding: 'Dieses Gebäude',
        yearsSinceRenovation: 'Jahre seit Renovierung',
      },
      noData: 'Keine Zeitachsendaten verfügbar',
      noDataMessage: 'Keine Bau-Zeitachsen-Daten verfügbar',
      phaseLabels: {
        noRecord: 'Keine Aufzeichnung',
        singlePhase: 'Einphasige Konstruktion',
      },
      renovationCategories: {
        dueSoon: {
          description: 'Das Gebäude könnte in naher Zukunft von einer Renovierung oder Modernisierung profitieren',
          label: 'Bald fällig',
          range: '15-30 Jahre',
        },
        good: {
          description: 'Renovierung ist relativ kürzlich; Gebäude sollte noch in gutem Zustand sein',
          label: 'Guter Zustand',
          range: '5-15 Jahre',
        },
        overdue: {
          description: 'Seit der letzten Renovierung ist erhebliche Zeit vergangen; grössere Renovierungsarbeiten sind wahrscheinlich erforderlich',
          label: 'Überfällig',
          range: '> 30 Jahre',
        },
        recentlyRenovated: {
          description: 'Gebäude wurde in den letzten 5 Jahren renoviert und ist wahrscheinlich in ausgezeichnetem Zustand',
          label: 'Kürzlich renoviert',
          range: '< 5 Jahre',
        },
      },
      renovationStatuses: {
        considerUpdates: 'Updates erwägen',
        goodCondition: 'Guter Zustand',
        noRenovation: 'Keine Renovierung',
        recentlyRenovated: 'Kürzlich renoviert',
        renovationNeeded: 'Renovierung erforderlich',
      },
    },
  },
  gwr: {
    buildingCategory: {
      "Bâtiment à usage partiel d'habitation": 'Gebäude mit teilweiser Wohnnutzung',
      "Bâtiment d'habitation avec usage annexe": 'Wohngebäude mit Nebennutzung',
      "Bâtiment exclusivement à usage d'habitation": 'Gebäude mit ausschliesslicher Wohnnutzung',
      "Bâtiment partiellement à usage d'habitation": 'Gebäude mit teilweiser Wohnnutzung',
      "Bâtiment sans usage d'habitation": 'Gebäude ohne Wohnnutzung',
      'Construction particulière': 'Sonderbau',
      'Habitation provisoire': 'Provisorische Unterkunft',
    },
    buildingClass: {
      'Autre bâtiment non classé ailleurs': 'Sonstige Hochbauten, anderweitig nicht genannt',
      "Autres bâtiments d'exploitation agricole": 'Andere landwirtschaftliche Gebäude',
      "Autres bâtiments d'hébergement de tourisme": 'Andere Gebäude für kurzfristige Beherbergung',
      'Autres bâtiments non classés ailleurs': 'Sonstige Hochbauten, anderweitig nicht genannt',
      "Autres bâtiments pour l'hébergement collectif": 'Andere Gebäude für die kollektive Unterkunft',
      "Autres bâtiments pour l'hébergement de tourisme": 'Andere Gebäude für kurzfristige Beherbergung',
      "Bâtiment à usage d'habitation non déterminé": 'Wohngebäude, nicht genauer bestimmt',
      'Bâtiment à usage mixte': 'Gebäude mit gemischter Nutzung',
      'Bâtiment administratif': 'Verwaltungsgebäude',
      'Bâtiment commercial': 'Handelsgebäude',
      "Bâtiment d'exploitation agricole": 'Landwirtschaftliches Betriebsgebäude',
      'Bâtiment de culte': 'Sakralgebäude',
      'Bâtiment industriel': 'Industriegebäude',
      'Bâtiment pour les transports et les communications': 'Gebäude für Verkehr und Nachrichtenwesen',
      'Bâtiment scolaire': 'Schulgebäude',
      'Bâtiments administratifs': 'Verwaltungsgebäude',
      'Bâtiments commerciaux': 'Gross- und Einzelhandelsgebäude',
      "Bâtiments d'exploitation agricole": 'Landwirtschaftliche Betriebsgebäude',
      'Bâtiments de culte': 'Sakralgebäude',
      'Bâtiments industriels': 'Industriegebäude',
      "Bâtiments pour la garde d'animaux": 'Gebäude für die Tierhaltung',
      'Bâtiments pour les cultures végétales': 'Gebäude für den Pflanzenbau',
      'Bâtiments scolaires': 'Schulgebäude',
      Garage: 'Garagengebäude',
      Garages: 'Garagengebäude',
      'Gares, aérogares, centraux téléphoniques': 'Bahnhöfe, Abfertigungsgebäude, Fernsprechvermittlungszentralen',
      'Habitat communautaire': 'Wohngebäude für Gemeinschaften',
      'Hôpital et établissement de santé': 'Krankenhäuser und Facheinrichtungen des Gesundheitswesens',
      'Hôpitaux et établissements de santé': 'Krankenhäuser und Facheinrichtungen des Gesundheitswesens',
      Hôtel: 'Hotel',
      Hôtels: 'Hotels',
      'Immeuble à trois logements et plus': 'Gebäude mit drei oder mehr Wohnungen',
      'Maison à deux logements': 'Gebäude mit zwei Wohnungen',
      'Maison individuelle': 'Einfamilienhaus',
      'Monument historique ou classé': 'Denkmäler oder unter Denkmalschutz stehende Bauwerke',
      'Monuments historiques ou classés': 'Denkmäler oder unter Denkmalschutz stehende Bauwerke',
      'Réservoir, silo et entrepôt': 'Behälter, Silos und Lagergebäude',
      'Réservoirs, silos et entrepôts': 'Behälter, Silos und Lagergebäude',
      'Salle de sport': 'Sporthalle',
      'Salle de tir': 'Schiessanlage',
      'Salles de sport': 'Sporthallen',
      'Salles de tir': 'Schiessanlagen',
    },
    buildingStatus: {
      autorisé: 'bewilligt',
      démoli: 'abgebrochen',
      'en construction': 'im Bau',
      existant: 'bestehend',
      inutilisable: 'nicht nutzbar',
      'non réalisé': 'nicht realisiert',
      projeté: 'projektiert',
    },
    energySource: {
      Air: 'Luft',
      Aucun: 'Keiner',
      Aucune: 'Keine',
      Autre: 'Andere',
      'Bois (bois déchiqueté, copeaux)': 'Holz (Hackschnitzel, Späne)',
      'Bois (bûches)': 'Holz (Hackschnitzel, Späne)',
      'Bois (générique)': 'Holz (alle Typen)',
      'Bois (granulés - pellets)': 'Holz (Pellets - Granulat)',
      'Capteurs solaires thermiques': 'Thermische Sonnenkollektoren',
      'Chaleur produite à distance (basse température)': 'Fernwärme (Niedertemperatur)',
      'Chaleur produite à distance (générique)': 'Fernwärme (alle Typen)',
      'Chaleur produite à distance (haute température)': 'Fernwärme (Hochtemperatur)',
      'Eau (nappe phréatique, eaux superficielles, eaux résiduelles)': 'Wasser (Grundwasser, Oberflächenwasser, Restwasser)',
      Electricité: 'Elektrizität',
      Gaz: 'Gas',
      Indéterminé: 'Unbestimmt',
      Mazout: 'Heizöl',
      'Pas de générateur': 'Kein Generator',
      'Pompe à chaleur': 'Wärmepumpe',
      'Rejets thermiques (dans le bâtiment)': 'Wärmeabgabe (im Gebäude)',
      'Serpentin géothermique': 'Geothermische Serpentine',
      'Soleil (thermique)': 'Sonne (thermisch)',
      'Sonde géothermique': 'Geothermische Sonde',
    },
    heaterGenerator: {
      Autre: 'Andere',
      'Boiler électrique central': 'Zentraler Elektroboiler',
      'Chaudière (générique)': 'Kessel (allgemein)',
      'Chaudière (générique) pour plusieurs bâtiments': 'Kessel (allgemein) für mehrere Gebäude',
      'Chaudière (générique) pour un seul bâtiment': 'Kessel (allgemein) für ein einzelnes Gebäude',
      'Chaudière à condensation': 'Brennwertkessel',
      'Chaudière à condensation pour plusieurs bâtiments': 'Brennwertkessel für mehrere Gebäude',
      'Chaudière à condensation pour un seul bâtiment': 'Brennwertkessel für ein einzelnes Gebäude',
      'Chaudière standard': 'Standardkessel',
      'Chaudière standard pour plusieurs bâtiments': 'Standardkessel für mehrere Gebäude',
      'Chaudière standard pour un seul bâtiment': 'Standardkessel für ein einzelnes Gebäude',
      'Chauffage central électrique': 'Zentrale Elektroheizung',
      'Chauffage central électrique pour plusieurs bâtiments': 'Zentrale Elektroheizung für mehrere Gebäude',
      'Chauffage central électrique pour un seul bâtiment': 'Zentrale Elektroheizung für ein einzelnes Gebäude',
      'Chauffage électrique direct (y compris radiateur infra-rouge)': 'Direkte Elektroheizung (inkl. Infrarot)',
      'Échangeur de chaleur (y compris pour la chaleur à distance)': 'Wärmetauscher (einschliesslich Fernwärme)',
      'Echangeur de chaleur (y.c. chauffage à distance)': 'Wärmetauscher (inkl. Fernwärme)',
      'Echangeur de chaleur (y.c. chauffage à distance) pour plusieurs bâtiments': 'Wärmetauscher für mehrere Gebäude',
      'Echangeur de chaleur (y.c. chauffage à distance) pour un seul bâtiment': 'Wärmetauscher für ein einzelnes Gebäude',
      Indéterminé: 'Unbestimmt',
      'Installation couplage chaleur-force': 'BHKW-Anlage',
      'Installation couplage chaleur-force pour plusieurs bâtiments': 'BHKW für mehrere Gebäude',
      'Installation couplage chaleur-force pour un seul bâtiment': 'BHKW für ein einzelnes Gebäude',
      'Installation solaire thermique': 'Solarthermie-Anlage',
      'Installation solaire thermique pour plusieurs bâtiments': 'Solarthermie für mehrere Gebäude',
      'Installation solaire thermique pour un seul bâtiment': 'Solarthermie für ein einzelnes Gebäude',
      'Pas de générateur de chaleur': 'Kein Wärmeerzeuger',
      Poêle: 'Ofen',
      'Pompe à chaleur PAC': 'Wärmepumpe',
      'Pompe à chaleur PAC pour plusieurs bâtiments': 'Wärmepumpe für mehrere Gebäude',
      'Pompe à chaleur PAC pour un seul bâtiment': 'Wärmepumpe für ein einzelnes Gebäude',
    },
  },
  ui: {
    exportPdf: {
      unavailable: 'Dieser PDF-Export ist nicht verfügbar. Schliessen Sie die Vorschau und versuchen Sie es erneut.',
    },
    aiFilter: {
      analyzing: 'Ihre Anfrage wird analysiert...',
      apply: 'Anwenden',
      clearHistory: 'Löschen',
      description: 'Beschreiben Sie in natürlicher Sprache, wonach Sie suchen, und die entsprechenden Filter werden angewendet.',
      errors: {
        microphoneAccess: 'Zugriff auf das Mikrofon nicht möglich. Bitte überprüfen Sie Ihre Berechtigungen.',
        textProcessing: 'Bei der Verarbeitung Ihrer Anfrage ist ein Fehler aufgetreten',
        voiceNotConfigured: 'Die Spracheingabe ist für diesen Filter nicht konfiguriert.',
        voiceProcessing: 'Bei der Verarbeitung Ihrer Sprachanfrage ist ein Fehler aufgetreten',
      },
      examplePrompts: {
        buildings: 'Immobilien gebaut nach 2020 mit Parkplatz',
        properties: 'Zeigen Sie mir Immobilien über 100m² im Stadtzentrum',
        transactions: 'Finden Sie aktuelle Transaktionen über 1 Million',
      },
      filtersApplied: 'Filter angewendet',
      filterSaved: 'Filter gespeichert',
      placeholder: 'Geben Sie Ihre Filteranfrage ein oder sprechen Sie sie...',
      recentSearches: 'Letzte Suchen',
      recording: 'Aufnahme...',
      saveAsFilter: 'Als Filter speichern',
      startRecording: 'Sprachaufnahme starten',
      stopRecording: 'Aufnahme stoppen',
      title: 'KI-Filterassistent',
      trySaying: 'Versuchen Sie zum Beispiel:',
    },
    analysisMap: {
      baseLayers: {
        building: 'Gebäude',
        building3d: 'Gebäude 3D',
        buildingNumbers: 'Gebäudenummern',
        chooseMapStyle: 'Kartenstil wählen',
        contours: 'Höhenlinien',
        dimensionMetrics: 'Massangaben',
        distanceLimit: 'Abstandsgrenze',
        footprint: 'Grundfläche',
        labels: 'Beschriftungen',
        land: 'Grundstück',
        mapBase: 'Kartengrundlage',
        mapFeatures: 'Kartenelemente',
        parcelBoundaries: 'Parzellengrenzen',
        pointCloud: 'Punktwolke',
        streets: 'Strassen',
        terrain: 'Gelände',
      },
      controls: {
        hideMap: 'Karte ausblenden',
      },
      groupLabels: {
        demographicsLayer: 'Demografieebene',
        employmentLayer: 'Beschäftigungsebene',
        historicalImagery: 'Historische Bildaufnahmen',
        noiseLayer: 'Lärmebene',
        purchaseMarketLayer: 'Kaufmarktebene',
        rentalMarketLayer: 'Mietmarktebene',
        zoningLayer: 'Zonierungsebene',
      },
      groupTitles: {
        amenities: 'Einrichtungen',
        buildingPermits: 'Baubewilligungen',
        demographics: 'Demografie',
        dynamicLayers: 'Dynamische Ebenen',
        employment: 'Beschäftigung',
        energy: 'Energie',
        historicalSatelliteImages: 'Historische Satellitenbilder',
        layers: 'Ebenen',
        naturalHazards: 'Naturgefahren',
        noiseAnalysis: 'Lärmanalyse',
        purchaseListings: 'Kaufinserate',
        purchaseMarket: 'Kaufmarkt',
        rentalListings: 'Mietinserate',
        rentalMarket: 'Mietmarkt',
        restrictions: 'Einschränkungen',
        sunlightStudy: 'Sonneneinstrahlung',
        transactions: 'Transaktionen',
        transportAccess: 'Transport & Zugang',
        zoning: 'Zonierung',
        zoningRules: 'Zonierungsbestimmungen',
      },
      hazardTypes: {
        avalanche: 'Lawine',
        flood: 'Hochwasser',
        landslide: 'Rutschung',
        rockslide: 'Sturz',
        title: 'Gefahrentypen',
      },
      layers: {
        amenitiesCulture: 'Kultur',
        amenitiesCultureDescription: 'Museen, Theater, Kultureinrichtungen',
        amenitiesDining: 'Gastronomie',
        amenitiesDiningDescription: 'Restaurants, Cafés, Bars',
        amenitiesEducation: 'Bildung',
        amenitiesEducationDescription: 'Schulen, Universitäten, Bibliotheken',
        amenitiesGrocery: 'Lebensmittel',
        amenitiesGroceryDescription: 'Supermärkte, Lebensmittelgeschäfte',
        amenitiesHealthcare: 'Gesundheit',
        amenitiesHealthcareDescription: 'Krankenhäuser, Kliniken, Apotheken',
        amenitiesServices: 'Dienstleistungen',
        amenitiesServicesDescription: 'Banken, Poststellen, öffentliche Dienste',
        amenitiesShopping: 'Einkaufen',
        amenitiesShoppingDescription: 'Einkaufszentren, Malls, Einzelhandelsgeschäfte',
        amenitiesTransport: 'Verkehr',
        amenitiesTransportDescription: 'Haltestellen und Bahnhöfe des öffentlichen Verkehrs',
        amenitiesWellness: 'Wellness',
        amenitiesWellnessDescription: 'Fitnessstudios, Spas, Wellnesszentren',
        buildingPermitsMarkers: 'Baubewilligungen',
        buildingPermitsMarkersDescription: 'Standorte der Baubewilligungen auf der Karte anzeigen',
        cyclingTravelTime: 'Fahrradfahrzeit',
        cyclingTravelTimeDescription: '5, 10, 15, 30 Minuten Fahrradzonen',
        demographicsAgeGroup: 'Dominante Altersgruppe',
        demographicsAgeGroupDescription: 'Dominantes demografisches Segment für zielgruppenspezifisches Marketing',
        demographicsElderlyPct: 'Ältere Bevölkerung (65+)',
        demographicsElderlyPctDescription: 'Nachfrage nach barrierefreiem Wohnraum und Gesundheitsversorgung in der Nähe',
        demographicsForeignPct: 'Ausländische Bevölkerung',
        demographicsForeignPctDescription: 'Indikator für die internationale Marktnachfrage',
        demographicsGrowthRate: 'Bevölkerungswachstum (5 Jahre)',
        demographicsGrowthRateDescription: 'Marktdynamik und Investitionspotenzial',
        demographicsHouseholdType: 'Dominanter Haushaltstyp',
        demographicsHouseholdTypeDescription: 'Nachfrage nach Wohnungsgrössen (Studios vs. Familienhäuser)',
        demographicsIsochrone: 'Analysegebiet',
        demographicsIsochroneDescription: 'Zeigt das Einzugsgebiet für die demografische Analyse',
        demographicsPopulationDensity: 'Bevölkerungsdichte',
        demographicsPopulationDensityDescription: 'Zentraler Urbanisierungsindikator für die Immobiliennachfrage',
        demographicsResidenceDuration: 'Dominante Wohndauer',
        demographicsResidenceDurationDescription: 'Indikator für Quartiersstabilität und Mieterfluktuation',
        demographicsYoungAdultPct: 'Junge Erwachsene (20-39 J.)',
        demographicsYoungAdultPctDescription: 'Stärke des Mietmarktes und Nachfrage nach Starterimmobilien',
        drivingTravelTime: 'Fahrzeit mit dem Auto',
        drivingTravelTimeDescription: '5, 10, 15, 30 Minuten Fahrzonen',
        employmentDensity: 'Beschäftigungsdichte',
        employmentDensityDescription: 'Indikator für Gewerbeimmobiliennachfrage und wirtschaftliche Aktivität',
        employmentDominantSector: 'Dominanter Beschäftigungssektor',
        employmentDominantSectorDescription: 'Wirtschaftscharakter (Primär/Sekundär/Tertiär)',
        employmentFulltimeRatio: 'Vollzeitbeschäftigungsquote',
        employmentFulltimeRatioDescription: 'Einkommensstabilität und Stärke der Wohnungsnachfrage',
        employmentGrowthRate: 'Beschäftigungswachstum (5 Jahre)',
        employmentGrowthRateDescription: 'Marktdynamik und Investitionspotenzial',
        employmentIsochrone: 'Analysegebiet',
        employmentIsochroneDescription: 'Zeigt das Einzugsgebiet für die Beschäftigungsanalyse',
        employmentJobsPerEstablishment: 'Arbeitsplätze je Betrieb',
        employmentJobsPerEstablishmentDescription: 'Unternehmensgrössenindikator (Konzerne vs. KMU)',
        employmentPublicPrivate: 'Öffentlicher vs. privater Sektor',
        employmentPublicPrivateDescription: 'Staatliche Präsenz und Mieterstabilität',
        listingsMarkers: 'Angebots-Marker',
        listingsMarkersDescription: 'Einzelne Immobilienangebote auf der Karte anzeigen',
        nearbyBuildings: 'Umliegende Gebäude',
        noiseRailwayDay: 'Bahnlärm - Tag',
        noiseRailwayDayDescription: 'Bahnlärmpegel tagsüber in dB',
        noiseRailwayNight: 'Bahnlärm - Nacht',
        noiseRailwayNightDescription: 'Bahnlärmpegel nachts in dB',
        noiseRoadDay: 'Strassenlärm - Tag',
        noiseRoadDayDescription: 'Strassenlärmpegel tagsüber in dB',
        noiseRoadNight: 'Strassenlärm - Nacht',
        noiseRoadNightDescription: 'Strassenlärmpegel nachts in dB',
        purchaseMarketApartmentPrice: 'Ø Kaufpreis/m² - Wohnung',
        purchaseMarketApartmentPriceDescription: 'Durchschnittlicher Kaufpreis pro Quadratmeter für Wohnungen in der Region',
        purchaseMarketHousePrice: 'Ø Kaufpreis/m² - Haus',
        purchaseMarketHousePriceDescription: 'Durchschnittlicher Kaufpreis pro Quadratmeter für Häuser in der Region',
        rentalMarketApartmentPrice: 'Ø Mietpreis/m² - Wohnung',
        rentalMarketApartmentPriceDescription: 'Durchschnittlicher Mietpreis pro Quadratmeter für Wohnungen in der Region',
        rentalMarketHousePrice: 'Ø Mietpreis/m² - Haus',
        rentalMarketHousePriceDescription: 'Durchschnittlicher Mietpreis pro Quadratmeter für Häuser in der Region',
        restrictionsBase: 'Einschränkungen',
        restrictionsBaseDescription: 'Nutzungsbeschränkungen und -auflagen für das Grundstück',
        shadowSimulation: 'Schattensimulation',
        shadowSimulationDescription: 'Echtzeit-Schattensimulation basierend auf Datum und Uhrzeit',
        solarPotential: 'Solarpotenzial',
        solarPotentialDescription: 'Dachflächen nach Solarpotenzial eingefärbt (kWh/m²/Jahr)',
        sunDirection: 'Sonnenrichtung',
        sunDirectionDescription: 'Zeigt Sonnenaufgang, Sonnenuntergang und aktuelle Sonnenrichtung als Linien vom Kartenzentrum',
        sunlightHours: 'Sonnenstunden',
        sunlightHoursDescription: 'Farbcodierte Darstellung der täglichen Sonnenstunden von Sonnenaufgang bis Sonnenuntergang',
        transactionPoints: 'Transaktionspunkte',
        transactionPointsDescription: 'Historische Immobilientransaktionen anzeigen',
        transportBusStops: 'Bushaltestellen',
        transportBusStopsDescription: 'Öffentliche Bushaltestellen im Umkreis von 1.5 km',
        transportEvCharging: 'E-Ladestationen',
        transportEvChargingDescription: 'Ladestationen für Elektrofahrzeuge im Umkreis von 5 km',
        transportHighwayAccess: 'Autobahnanschluss',
        transportHighwayAccessDescription: 'Autobahnanschlüsse im Umkreis von 15 km',
        transportTrainStops: 'Bahnhöfe',
        transportTrainStopsDescription: 'Bahnhöfe im Umkreis von 1.5 km',
        transportTramStops: 'Tramhaltestellen',
        transportTramStopsDescription: 'Tramhaltestellen im Umkreis von 1.5 km',
        walkingTravelTime: 'Gehzeit',
        walkingTravelTimeDescription: '5, 10, 15, 30 Minuten Gehzonen',
        zoningAll: 'Alle Zonentypen',
        zoningAllDescription: 'Alle Zonentypen anzeigen (Allgemein, Kantonal, Lokal, Teilweise)',
        zoningCantonalOnly: 'Nur Kantonale',
        zoningCantonalOnlyDescription: 'Nur kantonale Zonierungszonen anzeigen',
        zoningGeneralOnly: 'Nur Allgemeine',
        zoningGeneralOnlyDescription: 'Nur allgemeine Zonierungszonen anzeigen',
        zoningLocalOnly: 'Nur Lokale',
        zoningLocalOnlyDescription: 'Nur lokale Zonierungszonen anzeigen',
        zoningMapStyle: 'Zonierungskarte',
        zoningMapStyleDescription: 'Zonierung auf der Standardkarte anzeigen',
        zoningPartialOnly: 'Nur Teilweise',
        zoningPartialOnlyDescription: 'Nur teilweise Zonierungszonen anzeigen',
        zoningSatelliteMapStyle: 'Satellit-Zonierungskarte',
        zoningSatelliteMapStyleDescription: 'Zonierung auf Satellitenbildern anzeigen',
      },
      layersPanel: {
        closePanel: 'Ebenenpanel schliessen',
        disabledSelectLayer: 'Deaktiviert - Ebene auswählen',
        disableLayer: 'Ebene deaktivieren',
        hideAll: 'Alle ausblenden',
        opacity: 'Deckkraft',
        searchLayers: 'Ebenen suchen',
        searchPlaceholder: 'Ebenen suchen...',
        selectLayer: 'Auswählen...',
        showAll: 'Alle anzeigen',
        title: 'Kartenebenen',
      },
      legends: {
        cyclingTravelTime: 'Fahrradfahrzeit',
        drivingTravelTime: 'Fahrzeit mit dem Auto',
        min: '{{count}} Min.',
        walkingTravelTime: 'Gehzeit',
      },
      mapStyles: {
        light: 'Hell',
        lightDescription: 'Minimaler Stil für Datenvisualisierung',
        outdoors: 'Outdoor',
        outdoorsDescription: 'Outdoor-Karte mit Gelände und Wanderwegen',
        popetyio: 'Popety.io',
        popetyioDescription: 'Popety.io-Kartenstil für die Schweiz',
        satellite: 'Satellit',
        satelliteDescription: 'Luftbildaufnahmen mit Strassenüberlagerung',
        standard: 'Standard',
        standardDescription: '3D-Gebäude-Strassenkartenstil',
        traffic: 'Verkehr',
        trafficDescription: 'Karte mit Verkehrsbedingungen',
        zoning: 'Zonierung',
        zoningDescription: 'Benutzerdefinierter Stil mit Zonierungsfarben',
        zoningSatellite: 'Zonierung Satellit',
        zoningSatelliteDescription: 'Satellitenbilder mit Zonierungsüberlagerung',
      },
      restrictionTypes: 'Einschränkungstypen',
      sunDirectionLegend: {
        currentSun: 'Aktuelle Sonne',
        helpText: 'Linien vom Kartenzentrum zeigen die Sonnenrichtung an',
        sunrise: 'Sonnenaufgang',
        sunset: 'Sonnenuntergang',
        title: 'Sonnenrichtung',
      },
      sunlightControls: {
        date: 'Datum',
        now: 'Jetzt',
        spring: 'Frühling',
        summer: 'Sommer',
        sunExposureInfo: 'Tägliche Sonnenexposition von Sonnenaufgang bis Sonnenuntergang',
        time: 'Uhrzeit',
        timeline: 'Tagesverlauf',
        today: 'Heute',
        winter: 'Winter',
      },
    },
    common: {
      actions: {
        back: 'Zurück',
        cancel: 'Abbrechen',
        close: 'Schliessen',
        confirm: 'Bestätigen',
        delete: 'Löschen',
        edit: 'Bearbeiten',
        loadMore: 'Mehr laden',
        next: 'Weiter',
        save: 'Speichern',
        submit: 'Absenden',
        viewDetails: 'Details anzeigen',
      },
      no: 'Nein',
      status: {
        error: 'Ein Fehler ist aufgetreten',
        loading: 'Laden...',
        saving: 'Speichern...',
        success: 'Erfolg',
      },
      validation: {
        invalidEmail: 'Ungültige E-Mail-Adresse',
        invalidFormat: 'Ungültiges Format',
        maxLength: 'Maximal {{count}} Zeichen',
        minLength: 'Mindestens {{count}} Zeichen',
        required: 'Dieses Feld ist erforderlich',
      },
      yes: 'Ja',
    },
    dataTable: {
      actions: 'Aktionen',
      bulkActions: {
        exportAsExcel: 'Als Excel exportieren',
        itemsSelected: '{{count}} Einträge ausgewählt',
      },
      columns: {
        button: 'Spalten',
        dragHint: 'Tabellenköpfe ziehen, um Spalten neu anzuordnen',
        moveColumn: 'Spalte {{column}} verschieben',
        resetToDefault: 'Auf Standard zurücksetzen',
        showHide: 'Spalten anzeigen/ausblenden',
      },
      empty: {
        description: 'Versuchen Sie, Ihre Filter oder Suchkriterien anzupassen',
        title: 'Keine Daten gefunden',
      },
      error: {
        description: 'Beim Laden der Daten ist ein Fehler aufgetreten',
        title: 'Fehler beim Laden der Daten',
      },
      loading: 'Daten werden geladen...',
      mobile: {
        pullToRefresh: 'Zum Aktualisieren ziehen',
        refreshing: 'Wird aktualisiert...',
        releaseToRefresh: 'Loslassen zum Aktualisieren',
        selectColumn: 'Spalte auswählen',
        showLess: 'Weniger anzeigen',
        showMore: 'Mehr anzeigen',
        sortAscending: 'Aufsteigend sortieren',
        sortBy: 'Sortieren nach',
        sortDescending: 'Absteigend sortieren',
      },
      pagination: {
        items: 'Einträge',
        of: 'von',
        show: 'Anzeigen',
        showing: 'Anzeige von',
      },
    },
    detailPanel: {
      close: 'Schliessen',
      closePanel: 'Bereich schliessen',
      viewDetails: 'Details anzeigen',
    },
    drawingTools: {
      '3dMode': '3D-Modus',
      deleteAll: 'Alle löschen',
      deleteSelected: 'Ausgewählte löschen ({{count}})',
      drawLine: 'Linie zeichnen',
      drawPolygon: 'Polygon zeichnen',
      measureDistance: 'Entfernung messen',
      tooltip: 'Zeichenwerkzeuge',
    },
    files: {
      categories: {
        cadFiles: 'CAD-Dateien',
        documents: 'Dokumente',
        images: 'Bilder',
        other: 'Andere',
        pdfDocuments: 'PDF-Dokumente',
        spreadsheets: 'Tabellenkalkulationen',
      },
      confirmDelete: 'Diese Datei löschen?',
      deleteFile: 'Datei löschen',
      downloadFile: 'Datei herunterladen',
      emptyDescription: 'Laden Sie Dateien hoch, um wichtige Dokumente zu organisieren',
      emptyTitle: 'Keine Dateien hochgeladen',
      retry: 'Erneut versuchen',
      searchPlaceholder: 'Dateien suchen...',
      searchResults: '{{count}} Ergebnis von {{total}} Elementen',
      searchResultsPlural: '{{count}} Ergebnisse von {{total}} Elementen',
      time: {
        daysAgo: 'Vor {{count}}T',
        hoursAgo: 'Vor {{count}}h',
        justNow: 'Gerade eben',
        lastWeek: 'Letzte Woche',
        minutesAgo: 'Vor {{count}} Min.',
        weeksAgo: 'Vor {{count}} Wochen',
        yesterday: 'Gestern',
      },
      title: 'Dateien',
      uploadFile: 'Datei hochladen',
      uploadHint: 'PDF, DOC, XLS, DWG, Bilder (max {{maxSize}}MB)',
      uploadText: 'Klicken zum Hochladen oder Dateien hierher ziehen',
    },
    filters: {
      filterContainer: {
        clearAll: 'Alle löschen',
        hideFilters: 'Filter ausblenden',
        lessFilters: 'Weniger Filter',
        loading: 'Filter werden geladen...',
        moreFilters: 'Weitere Filter',
        resultsFound: '{{count}} Ergebnisse gefunden',
        showFilters: 'Filter anzeigen',
      },
      filterPanel: {
        apply: 'Anwenden',
        clear: 'Löschen',
        emptyState: 'Filteroptionen werden hier angezeigt',
        filters: 'Filter',
        invalidRange: 'Minimum muss kleiner als Maximum sein — die Ergebnisse werden nicht aktualisiert',
        reset: 'Zurücksetzen',
        resetFilters: 'Filter zurücksetzen',
        toggleFilters: 'Filter umschalten',
      },
      quickFilters: {
        activeFilters: 'Aktive Filter',
        removeFilter: 'Filter {{label}} entfernen',
        viewLess: 'Weniger anzeigen',
        viewMore: '+{{count}} weitere',
      },
    },
    forgotPassword: {
      backToLogin: 'Zurück zur Anmeldung',
      description: 'Geben Sie Ihre E-Mail-Adresse ein und wir senden Ihnen einen Link zum Zurücksetzen Ihres Passworts.',
      emailLabel: 'E-Mail-Adresse',
      emailPlaceholder: 'E-Mail eingeben',
      emailRequired: 'E-Mail ist erforderlich',
      errorMessage: 'Ein Fehler ist aufgetreten. Bitte versuchen Sie es erneut.',
      invalidEmail: 'Bitte geben Sie eine gültige E-Mail-Adresse ein',
      sending: 'Senden...',
      sendLink: 'Link senden',
      submitButton: 'Neues Passwort senden',
      success: 'Überprüfen Sie Ihre E-Mail auf einen Link zum Zurücksetzen Ihres Passworts.',
      successMessage: 'Falls die E-Mail existiert, wurde ein neues Passwort an Ihre Adresse gesendet.',
      title: 'Passwort zurücksetzen',
    },
    locationFilter: {
      clear: 'Standort löschen',
      noResults: 'Keine Standorte gefunden',
      placeholder: 'Standort suchen...',
      types: {
        address: 'Adresse',
        district: 'Bezirk',
        municipality: 'Gemeinde',
        postalCode: 'PLZ',
        region: 'Region',
      },
    },
    login: {
      continueWithGoogle: 'Mit Google fortfahren',
      email: 'E-Mail',
      emailPlaceholder: 'E-Mail eingeben',
      forgotPassword: 'Passwort vergessen?',
      noAccount: 'Noch kein Konto?',
      orContinueWith: 'Oder weiter mit',
      password: 'Passwort',
      passwordPlaceholder: 'Passwort eingeben',
      rememberMe: 'Angemeldet bleiben',
      signIn: 'Anmelden',
      signingIn: 'Anmeldung...',
      signUp: 'Registrieren',
      title: 'Anmelden',
    },
    map: {
      controls: {
        fullscreen: 'Vollbild',
        myLocation: 'Mein Standort',
        resetView: 'Ansicht zurücksetzen',
        zoomIn: 'Vergrössern',
        zoomOut: 'Verkleinern',
      },
      layers: {
        satellite: 'Satellit',
        streets: 'Strassen',
        terrain: 'Gelände',
      },
      vectorTile: {
        error: 'Fehler beim Laden der Kartendaten: {{error}}',
        featuresAtLocation: '{{count}} Objekte an diesem Standort',
        listingsAtLocation: '{{count}} Inserate an diesem Standort',
        loading: 'Kartendaten werden geladen...',
        overlapCount: '{{count}} weitere. Klicken, um alle anzuzeigen',
        scrollFeatures: 'Scrollen, um alle {{count}} Objekte anzusehen',
        scrollListings: 'Scrollen, um alle {{count}} Inserate anzusehen',
      },
    },
    metricSourceModal: {
      agreement: {
        conflict: 'Konflikt — 2 Quellen stimmen bei diesem Wert nicht überein',
        userModified: 'Vom Benutzer geändert — Originalwert von {{source}}: {{value}}',
      },
      badges: {
        estimate: 'SCHÄTZ',
        geoportal: 'GEO',
        llm: 'REG',
        manual: 'EDIT',
        old: 'ALT',
      },
      boundSelector: {
        ariaLabel: 'Min oder Max auswählen',
        max: 'MAX',
        min: 'MIN',
      },
      estimate: {
        dotStripLabel: 'Verteilung von {{metric}} über {{count}} Grundstücke — grössere Punkte = mehr Grundstücke',
        dotTooltip: '{{count}} Grundstück(e) in diesem Bereich',
        legendEstimate: 'Geschätzter Wert ({{value}})',
        legendMedian: 'Median ({{value}})',
        legendParcel: '1 Grundstück',
        legendParcelScale: 'Weniger / mehr Grundstücke',
        loading: 'Statistiken werden geladen...',
        noBuildings: 'Keine Gebäude in dieser Zone gefunden, um Statistiken zu berechnen',
        rangeText: 'Die Werte in dieser Zone reichen von {{min}} bis {{max}} (Median: {{median}})',
        step1Desc: '{{buildingCount}} Gebäude auf {{landCount}} Grundstücken wurden für die Berechnung verwendet',
        step1Title: 'Wir haben die bestehenden Gebäude in dieser Zone analysiert',
        step2Desc: {
          cos: 'Gebäudegrundfläche geteilt durch Grundstücksfläche',
          cus: 'Gesamte Geschossfläche geteilt durch Grundstücksfläche',
          floor_nb: 'Maximale Anzahl Stockwerke pro Gebäude auf jedem Grundstück',
          height: 'Maximale Gebäudehöhe auf jedem Grundstück',
          ibus: 'Bruttogeschossfläche geteilt durch Grundstücksfläche',
          im: 'Gebäudemasse (Volumen) im Verhältnis zur Grundstücksfläche',
          unit_nb: 'Maximale Anzahl Einheiten pro Gebäude auf jedem Grundstück',
          vbr: 'Gebäudevolumen geteilt durch Grundstücksfläche',
        },
        step2Title: '{{metric}} wurde für jedes Grundstück berechnet',
        step3Desc: '{{percentile}}% der Grundstücke haben einen {{metric}} von höchstens {{value}}',
        step3Title: 'Der Wert spiegelt wider, was die meisten Grundstücke erlauben',
        title: 'Wie wurde dieser Wert berechnet?',
      },
      geoportal: {
        openTab: 'In neuem Tab öffnen',
        title: 'Kantonales Geoportal',
        viewLink: 'Im kantonalen Geoportal anzeigen',
      },
      labels: {
        confirmedValue: 'Bestätigter Wert',
        displayedValue: 'Angezeigter Wert',
        maximumValue: 'Maximalwert',
        minimumValue: 'Minimalwert',
        userValue: 'Benutzerwert',
        value: 'Wert',
      },
      noData: {
        message: 'Kein Wert aus einer Quelle für diese Kennzahl verfügbar.',
      },
      noDocument: {
        message: 'Kein Referenzdokument für diese Quelle verfügbar.',
      },
      reference: {
        article: 'Artikel',
        articlePath: 'Artikelpfad',
        document: 'Dokument',
        documentTitle: 'Dokumenttitel',
        pages: 'Seite(n)',
        viewPdf: 'PDF ansehen',
      },
      report: {
        cancel: 'Abbrechen',
        contextTitle: 'Wertdetails',
        error: 'Meldung konnte nicht gesendet werden. Bitte versuchen Sie es erneut.',
        explanation: 'Was ist falsch?',
        explanationPlaceholder: 'Beschreiben Sie, was falsch ist und die korrekte Information...',
        link: 'Falschen Wert melden',
        submit: 'Meldung senden',
        success: 'Meldung erfolgreich gesendet. Vielen Dank!',
        suggestedValue: 'Korrekter Wert (optional)',
        suggestedValuePlaceholder: 'Geben Sie den korrekten Wert ein',
        title: 'Falschen Wert melden',
      },
      sections: {
        additionalInfo: 'Zusätzliche Informationen',
        maxAdditionalInfo: 'Maximalwert - Zusätzliche Informationen',
        minAdditionalInfo: 'Minimalwert - Zusätzliche Informationen',
        referenceInfo: 'Referenzinformationen',
      },
      sources: {
        estimate: 'Geschätzt',
        geoportal: 'Geoportal',
        llm: 'Popety.io Research Lab',
        manual: 'Manuelle Überschreibung',
        old: 'Altdaten',
      },
      status: {
        notApplicable: 'Nicht zutreffend',
        seeReference: 'Siehe Referenz',
      },
      titleDetails: 'Details',
      titleRange: 'Bereich',
      tooltips: {
        estimate: 'Geschätzt durch Popety.io Algorithmen',
        geoportal: 'Offizielles Geoportal',
        llm: 'Aus dem Reglement extrahiert',
        manual: 'Vom Benutzer ersetzt',
        old: 'Altdaten',
      },
      userOverride: {
        description: 'Dieser Wert wurde vom Benutzer manuell geändert. Klicken Sie auf den Tab {{source}}, um die Originalquellendetails zu sehen.',
        noAuthoritativeSource: 'Keine massgebliche Quelle (Verordnung oder Geoportal) war für diese Kennzahl verfügbar.',
        timeline: {
          step1Desc: 'Quelle {{source}} : {{value}}',
          step1NoSource: 'Keine Originalquelle verfügbar',
          step1Title: 'Vom Algorithmus geschätzter Wert',
          step1TitleGeo: 'Wert vom Geoportal',
          step1TitleReg: 'Wert aus der Verordnung',
          step2Desc: 'Der {{source}}-Wert ({{originalValue}}) wurde manuell ersetzt',
          step2DescNoSource: 'Ein manueller Wert wurde vom Benutzer festgelegt',
          step2Title: 'Der Benutzer hat den Wert auf {{value}} geändert',
          step3Desc: 'Differenz: {{diff}} im Vergleich zur Schätzung',
          step3DescNoOriginal: 'Manuell festgelegter Wert',
          step3Title: 'Der verwendete Wert ist {{value}}',
          title: 'Wie wurde dieser Wert definiert?',
        },
      },
      warnings: {
        estimate: 'Dieser Wert wurde mit unserem internen Modell basierend auf ähnlichen Grundstücken in der Umgebung geschätzt. Bitte beachten Sie, dass dies eine Schätzung ist und möglicherweise nicht den tatsächlich zulässigen Wert widerspiegelt. Für genauere Informationen wenden Sie sich bitte an die offiziellen Zonendokumente oder die örtlichen Behörden.',
        estimateShort: 'Dieser Wert wurde von Popety.io-Algorithmen mittels räumlicher Analyse von Parzellen in derselben Zone geschätzt. Er kann von den offiziellen Zonendokumenten abweichen. Überprüfen Sie immer bei den lokalen Behörden.',
        geoportalShort: 'Dieser Wert stammt aus dem offiziellen kantonalen Geoportal. Abweichungen können auftreten, wenn Vorschriften kürzlich geändert wurden. Bitte überprüfen Sie die offiziellen Zonendokumente oder die örtlichen Behörden.',
        llm: 'Dieser Wert wurde vom Popety.io Research Lab mit fortschrittlichen Algorithmen und manueller Arbeit recherchiert und bereitgestellt. Obwohl wir uns um Genauigkeit bemühen, überprüfen Sie diese Informationen bitte anhand der offiziellen Zonendokumente oder bei den örtlichen Behörden, um die Einhaltung sicherzustellen.',
        regulationShort: 'Dieser Wert wurde vom Popety.io Research Lab mit fortschrittlichen Algorithmen und manueller Arbeit recherchiert und bereitgestellt. Obwohl wir uns um Genauigkeit bemühen, überprüfen Sie bitte die offiziellen Zonendokumente oder die örtlichen Behörden.',
      },
    },
    pdfViewer: {
      errors: {
        defaultMessage: 'Beim Laden des PDF-Dokuments ist ein Fehler aufgetreten. Bitte versuchen Sie es erneut.',
        loadFailed: 'PDF konnte nicht geladen werden',
        noUrl: 'Keine PDF-URL angegeben',
        pageNotFound: 'Seite nicht gefunden',
        retry: 'Erneut versuchen',
        tryAgain: 'Erneut versuchen',
      },
      loading: {
        text: 'PDF-Dokument wird geladen...',
      },
      search: {
        closeSearch: 'Suche schliessen (Esc)',
        nextMatch: 'Nächster Treffer (Enter)',
        noMatches: 'Keine Treffer',
        noResults: 'Keine Ergebnisse gefunden',
        previousMatch: 'Vorheriger Treffer (Shift+Enter)',
        resultsCount: '{{current}} von {{total}}',
      },
      toolbar: {
        download: 'Herunterladen',
        downloadPdf: 'PDF herunterladen',
        exitFullscreen: 'Vollbild beenden',
        fullscreen: 'Vollbild',
        nextPage: 'Nächste Seite',
        of: 'von',
        page: 'Seite',
        previousPage: 'Vorherige Seite',
        resetZoom: 'Zoom zurücksetzen',
        rotateLeft: 'Nach links drehen',
        rotateRight: 'Nach rechts drehen',
        search: 'Suchen',
        searchPlaceholder: 'Im Dokument suchen...',
        searchShortcut: 'Suchen (Strg+F)',
        zoomIn: 'Vergrössern',
        zoomOut: 'Verkleinern',
      },
    },
    propertyAttributes: {
      categories: {
        amenities: 'Ausstattung & Merkmale',
        basic: 'Grundinformationen',
        condition: 'Zustand & Qualität',
        energy: 'Energie & Systeme',
        environment: 'Umgebung & Lage',
        physical: 'Physische Eigenschaften',
      },
      empty: 'Keine Immobilienattribute verfügbar.',
      fields: {
        airConditioning: 'Klimaanlage',
        altitude: 'Höhe',
        attic: 'Dachboden',
        balcony: 'Balkonfläche',
        buildingHeight: 'Gebäudehöhe',
        cellar: 'Keller',
        condition: 'Allgemeinzustand',
        constructionYear: 'Baujahr',
        distanceToPowerlines: 'Entfernung zu Stromleitungen',
        elevator: 'Aufzug',
        energyClass: 'Energieklasse',
        exposureOrientation: 'Ausrichtung',
        facadeCondition: 'Fassadenzustand',
        fireplace: 'Kamin',
        floor: 'Stockwerk',
        floorArea: 'Geschossfläche',
        garage: 'Garagenplätze',
        garden: 'Gartenfläche',
        heatConsumptionIndex: 'Wärmeverbrauchsindex',
        heatingSystemAge: 'Alter der Heizungsanlage',
        heatingType: 'Heizungsart',
        lakeView: 'Seeblick',
        landArea: 'Grundstücksfläche',
        lastMaintenanceYear: 'Letztes Wartungsjahr',
        livingArea: 'Wohnfläche',
        minergie: 'Minergie zertifiziert',
        mountainView: 'Bergblick',
        noiseLevel: 'Lärmpegel',
        numberOfBathrooms: 'Badezimmer',
        numberOfBedrooms: 'Schlafzimmer',
        numberOfFloors: 'Anzahl Stockwerke',
        parking: 'Parkplätze',
        pool: 'Swimming Pool',
        propertyType: 'Immobilientyp',
        publicTransportQuality: 'Qualität öffentlicher Verkehr',
        renovationYear: 'Renovierungsjahr',
        roofCondition: 'Dachzustand',
        roomsNb: 'Zimmeranzahl',
        sauna: 'Sauna',
        slope: 'Hangneigung',
        solarArea: 'Solarfläche',
        solarPanelsInstalled: 'Solaranlagen installiert',
        solarPotential: 'Solarpotential',
        terrace: 'Terrassenfläche',
        travelTimeToCenters: 'Reisezeit zu Zentren',
        usableArea: 'Nutzfläche',
        volume: 'Volumen',
        windowsCondition: 'Fensterzustand',
      },
      units: {
        cubicMeters: 'm³',
        meters: 'm',
        rooms: 'Zimmer',
        spaces: 'Plätze',
        squareMeters: 'm²',
        years: 'Jahre',
      },
    },
    propertyAttributesForm: {
      buttons: {
        cancel: 'Abbrechen',
        save: 'Änderungen speichern',
        saving: 'Speichern...',
      },
      categories: {
        amenities: 'Ausstattung & Merkmale',
        basic: 'Grundinformationen',
        condition: 'Zustand & Qualität',
        energy: 'Energie & Systeme',
        environment: 'Umgebung & Lage',
        physical: 'Physische Eigenschaften',
      },
      fields: {
        airConditioning: 'Klimaanlage',
        altitude: 'Höhe',
        attic: 'Dachboden',
        balcony: 'Balkonfläche',
        buildingHeight: 'Gebäudehöhe',
        cellar: 'Keller',
        condition: 'Allgemeinzustand',
        constructionYear: 'Baujahr',
        distanceToPowerlines: 'Entfernung zu Stromleitungen',
        elevator: 'Aufzug',
        energyClass: 'Energieklasse',
        exposureOrientation: 'Ausrichtung',
        facadeCondition: 'Fassadenzustand',
        fireplace: 'Kamin',
        floor: 'Stockwerk',
        floorArea: 'Geschossfläche',
        garage: 'Garagenplätze',
        garden: 'Gartenfläche',
        heatConsumptionIndex: 'Wärmeverbrauchsindex',
        heatingSystemAge: 'Alter der Heizungsanlage',
        heatingType: 'Heizungsart',
        lakeView: 'Seeblick-Qualität',
        landArea: 'Grundstücksfläche',
        lastMaintenanceYear: 'Letztes Wartungsjahr',
        livingArea: 'Wohnfläche',
        minergie: 'Minergie zertifiziert',
        mountainView: 'Bergblick-Qualität',
        noiseLevel: 'Lärmpegel',
        numberOfBathrooms: 'Anzahl Badezimmer',
        numberOfBedrooms: 'Anzahl Schlafzimmer',
        numberOfFloors: 'Anzahl Stockwerke',
        parking: 'Parkplätze',
        pool: 'Swimming Pool',
        propertyType: 'Immobilientyp',
        publicTransportQuality: 'Qualität öffentlicher Verkehr',
        renovationYear: 'Renovierungsjahr',
        roofCondition: 'Dachzustand',
        roomsNb: 'Zimmeranzahl',
        sauna: 'Sauna',
        slope: 'Hangneigung',
        solarArea: 'Solarfläche',
        solarPanelsInstalled: 'Solaranlagen installiert',
        solarPotential: 'Solarpotential',
        terrace: 'Terrassenfläche',
        travelTimeToCenters: 'Reisezeit zu Zentren',
        usableArea: 'Nutzfläche',
        volume: 'Volumen',
        windowsCondition: 'Fensterzustand',
      },
      options: {
        heatingType: {
          districtHeating: 'Fernwärme',
          electricity: 'Elektrizität',
          gas: 'Gas',
          heatPump: 'Wärmepumpe',
          oil: 'Öl',
          other: 'Andere',
          pellets: 'Pellets',
          solarThermal: 'Solarthermie',
          wood: 'Holz',
        },
        level: {
          high: 'Hoch',
          low: 'Niedrig',
          medium: 'Mittel',
        },
        quality: {
          bad: 'Schlecht',
          good: 'Gut',
          medium: 'Mittel',
        },
        select: 'Auswählen...',
        viewQuality: {
          excellent: 'Ausgezeichnet',
          good: 'Gut',
          none: 'Keine',
          partial: 'Teilweise',
        },
      },
      placeholders: {
        altitude: 'z.B. 450',
        balcony: 'z.B. 12',
        buildingHeight: 'z.B. 12',
        condition: 'z.B. ausgezeichnet, gut, mittelmässig',
        constructionYear: 'z.B. 1985',
        distanceToPowerlines: 'z.B. 200',
        exposureOrientation: 'z.B. Süden, Südwesten',
        facadeCondition: 'z.B. ausgezeichnet, gut, mittelmässig',
        floor: 'z.B. 3',
        floorArea: 'z.B. 100',
        garage: 'z.B. 1',
        garden: 'z.B. 200',
        heatConsumptionIndex: 'z.B. 80',
        heatingSystemAge: 'z.B. 10',
        landArea: 'z.B. 500',
        lastMaintenanceYear: 'z.B. 2020',
        livingArea: 'z.B. 120',
        numberOfBathrooms: 'z.B. 2',
        numberOfBedrooms: 'z.B. 3',
        numberOfFloors: 'z.B. 4',
        parking: 'z.B. 2',
        propertyType: 'z.B. Wohnung, Haus, Villa',
        renovationYear: 'z.B. 2015',
        roofCondition: 'z.B. neu, gut, reparaturbedürftig',
        roomsNb: 'z.B. 4.5',
        slope: 'z.B. flach, leicht, steil',
        solarArea: 'z.B. 30',
        terrace: 'z.B. 25',
        travelTimeToCenters: 'z.B. 15 Minuten zum Stadtzentrum',
        usableArea: 'z.B. 140',
        volume: 'z.B. 450',
        windowsCondition: 'z.B. Doppelverglasung, neu',
      },
      units: {
        cubicMeters: 'm³',
        meters: 'm',
        squareMeters: 'm²',
        years: 'Jahre',
      },
    },
    propertyDescriptionEditor: {
      buttons: {
        cancel: 'Abbrechen',
        edit: 'Bearbeiten',
        editWithAi: 'Mit KI bearbeiten',
        save: 'Speichern',
        saving: 'Speichern...',
      },
      characters: 'Zeichen',
      emptyState: 'Klicken Sie auf "Mit KI bearbeiten", um schnell eine gut strukturierte Beschreibung zu erstellen und die Immobilienattribute automatisch auszufüllen, oder klicken Sie auf "Bearbeiten", um eine manuell hinzuzufügen.',
      recommended: 'Empfohlen: {{min}}-{{max}} Zeichen',
      title: 'Immobilienbeschreibung',
    },
    propertyDetails: {
      attributes: {
        empty: 'Es wurden noch keine Immobilienattribute hinzugefügt.',
        title: 'Attribute',
      },
      buttons: {
        edit: 'Bearbeiten',
        editWithAI: 'Mit KI bearbeiten',
        generateWithAI: 'Mit KI generieren',
        generating: 'Generieren...',
      },
      description: {
        empty: 'Es wurde noch keine Beschreibung hinzugefügt.',
        title: 'Immobilienbeschreibung',
      },
      situationLocation: {
        empty: 'Es wurde noch keine Lage- und Standortbeschreibung hinzugefügt.',
        title: 'Lage & Standort',
      },
      subtitle: 'Detaillierte Beschreibung und Informationen zur Immobilie.',
      title: 'Immobiliendetails',
    },
    reportModal: {
      cancel: 'Abbrechen',
      confirmDelete: 'Sind Sie sicher, dass Sie diesen Bericht löschen möchten?',
      createNew: 'Neuen Bericht erstellen',
      delete: 'Löschen',
      edit: 'Bearbeiten',
      noReports: 'Noch keine Berichte erstellt',
      sharedWithOffice: 'Mit dem Büro geteilt',
      title: 'PDF-Berichte',
      typeBuildingAnalysis: 'Gebäudeanalyse',
      typeEstimate: 'Schätzung',
      typeLandAnalysis: 'Grundstücksanalyse',
      typeLandGroupingAnalysis: 'Analyse einer Grundstücksgruppe',
      untitledReport: 'Unbenannter Bericht',
      updated: 'Aktualisiert',
    },
    savedItems: {
      buildingPermits: {
        empty: 'Keine gespeicherten Baugenehmigungen',
        remove: 'Aus Gespeicherten entfernen',
        title: 'Gespeicherte Baugenehmigungen',
      },
      listings: {
        empty: 'Keine gespeicherten Angebote',
        remove: 'Aus Gespeicherten entfernen',
        title: 'Gespeicherte Angebote',
      },
      transactions: {
        empty: 'Keine gespeicherten Transaktionen',
        remove: 'Aus Gespeicherten entfernen',
        title: 'Gespeicherte Transaktionen',
      },
    },
    tags: {
      addExistingTag: 'Bestehendes Tag hinzufügen',
      addSelectedTag: 'Ausgewähltes Tag hinzufügen',
      addTags: 'Tags hinzufügen',
      cancel: 'Abbrechen',
      createAndAdd: 'Erstellen und hinzufügen',
      createNewTag: 'Neues Tag erstellen',
      currentTags: 'Aktuelle Tags',
      manageTitle: 'Tags verwalten',
      noAvailableTags: 'Keine verfügbaren Tags',
      noMatchingTags: 'Keine passenden Tags gefunden',
      noTagsYet: 'Noch keine Tags zugewiesen',
      primary: 'Primär',
      primaryConfirm: 'Das bestehende primäre Tag wird in sekundär geändert. Fortfahren?',
      primaryWarning: 'Das bestehende primäre Tag wird sekundär',
      searchPlaceholder: 'Tags suchen...',
      secondary: 'Sekundär',
      setPrimary: 'Als primäres Tag festlegen',
      tagNamePlaceholder: 'Tag-Name...',
    },
    tooltipMetric: {
      categories: 'Kategorien:',
      howCalculated: 'Wie es berechnet wird:',
      whatItMeans: 'Was es bedeutet:',
      whyItMatters: 'Warum es wichtig ist:',
    },
  },
} satisfies AppTranslations
